<template>
  <div class="assets-manager">
    <div class="assets-container">
      <!-- Type Filter Tabs -->
      <div v-if="selectedProjectId" class="type-tabs">
        <button
          v-for="type in assetTypes"
          :key="type.value"
          class="type-tab"
          :class="{ active: selectedType === type.value }"
          @click="selectedType = type.value; loadAssets()"
        >
          {{ type.label }}
        </button>
      </div>

      <!-- Assets Content -->
      <div v-if="selectedProjectId" class="assets-content">
        <!-- Loading State -->
        <div v-if="isLoading" class="loading-overlay">
          <n-spin size="large" />
        </div>

        <!-- Assets Grid (all types: ALL, MOOD, BG, etc.) -->
        <div class="assets-grid-section" :class="{ 'with-text-editor': selectedType === 'text-style' || selectedType === 'text-copy', 'with-actions': selectedType === 'bg' || selectedType === 'logo' || selectedType === 'photo' || selectedType === 'reference' }">
          <!-- Text Editor Panel (right third) - Always visible for text types -->
          <div v-if="selectedType === 'text-style' || selectedType === 'text-copy'" class="text-editor-panel">
            <div class="text-editor-header">
              <h3>{{ selectedType === 'text-style' ? 'Create Text Style' : 'Create Text Copy' }}</h3>
            </div>
            <div class="text-editor-content">
              <!-- Style Form -->
              <n-form v-if="selectedType === 'text-style'" :model="textStyleForm" ref="textStyleFormRef">
                <n-form-item label="Name">
                  <n-input
                    v-model:value="textStyleForm.name"
                    placeholder="e.g., H1, H2, Headers, Body..."
                  />
                </n-form-item>
                <n-form-item label="Sample Text">
                  <n-input
                    v-model:value="textStyleForm.sampleText"
                    type="textarea"
                    :rows="4"
                    placeholder="Enter sample text to preview..."
                  />
                </n-form-item>
                <div class="text-settings-grid">
                  <n-form-item label="Font Family">
                    <n-select
                      v-model:value="textStyleForm.fontFamily"
                      :options="fontFamilyOptions"
                      placeholder="Select font"
                    />
                  </n-form-item>
                  <n-form-item label="Font Size">
                    <n-input-number
                      v-model:value="textStyleForm.fontSize"
                      :min="8"
                      :max="200"
                      :step="1"
                    />
                  </n-form-item>
                  <n-form-item label="Font Weight">
                    <n-select
                      v-model:value="textStyleForm.fontWeight"
                      :options="fontWeightOptions"
                      placeholder="Select weight"
                    />
                  </n-form-item>
                  <n-form-item label="Color">
                    <n-color-picker v-model:value="textStyleForm.color" />
                  </n-form-item>
                  <n-form-item label="Text Align">
                    <n-button-group>
                      <n-button
                        :type="textStyleForm.textAlign === 'left' ? 'primary' : 'default'"
                        @click="textStyleForm.textAlign = 'left'"
                        title="Align Left"
                      >
                        <template #icon>
                          <n-icon>
                            <svg viewBox="0 0 24 24">
                              <path fill="currentColor" d="M3,3H21V5H3V3M3,7H15V9H3V7M3,11H21V13H3V11M3,15H15V17H3V15M3,19H21V21H3V19Z" />
                            </svg>
                          </n-icon>
                        </template>
                      </n-button>
                      <n-button
                        :type="textStyleForm.textAlign === 'center' ? 'primary' : 'default'"
                        @click="textStyleForm.textAlign = 'center'"
                        title="Align Center"
                      >
                        <template #icon>
                          <n-icon>
                            <svg viewBox="0 0 24 24">
                              <path fill="currentColor" d="M3,3H21V5H3V3M5,7H19V9H5V7M3,11H21V13H3V11M5,15H19V17H5V15M3,19H21V21H3V19Z" />
                            </svg>
                          </n-icon>
                        </template>
                      </n-button>
                      <n-button
                        :type="textStyleForm.textAlign === 'right' ? 'primary' : 'default'"
                        @click="textStyleForm.textAlign = 'right'"
                        title="Align Right"
                      >
                        <template #icon>
                          <n-icon>
                            <svg viewBox="0 0 24 24">
                              <path fill="currentColor" d="M3,3H21V5H3V3M9,7H21V9H9V7M3,11H21V13H3V11M9,15H21V17H9V15M3,19H21V21H3V19Z" />
                            </svg>
                          </n-icon>
                        </template>
                      </n-button>
                    </n-button-group>
                  </n-form-item>
                </div>
                <div class="text-preview" v-if="textStyleForm.sampleText">
                  <div class="preview-label">Preview:</div>
                  <div 
                    class="preview-text"
                    :style="{
                      fontFamily: textStyleForm.fontFamily,
                      fontSize: `${textStyleForm.fontSize}px`,
                      fontWeight: textStyleForm.fontWeight,
                      color: textStyleForm.color,
                      textAlign: textStyleForm.textAlign
                    }"
                  >
                    {{ textStyleForm.sampleText }}
                  </div>
                </div>
                <div class="text-editor-actions">
                  <n-button @click="resetTextStyleForm">Reset</n-button>
                  <n-button type="primary" @click="createTextStyle" :loading="isCreatingTextBlock">
                    Create Style
                  </n-button>
                </div>
              </n-form>

              <!-- Copy Form -->
              <n-form v-else-if="selectedType === 'text-copy'" :model="textCopyForm" ref="textCopyFormRef">
                <n-form-item label="Text Set Name">
                  <n-input
                    v-model:value="textCopyForm.name"
                    placeholder="e.g., Exclamations, Captions, Headers..."
                  />
                </n-form-item>
                <n-form-item label="Text Items">
                  <div class="copy-items-list">
                    <div 
                      v-for="(_, index) in textCopyForm.items" 
                      :key="index" 
                      class="copy-item-row"
                    >
                      <n-input
                        v-model:value="textCopyForm.items[index]"
                        placeholder="Enter text item..."
                        @keydown.enter.prevent="addCopyItem(index)"
                      />
                      <n-button 
                        quaternary 
                        type="error" 
                        @click="removeCopyItem(index)"
                        :disabled="textCopyForm.items.length <= 1"
                      >
                        <template #icon>
                          <n-icon>
                            <svg viewBox="0 0 24 24">
                              <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                            </svg>
                          </n-icon>
                        </template>
                      </n-button>
                    </div>
                    <n-button 
                      quaternary 
                      type="primary" 
                      @click="addCopyItem(-1)"
                      style="width: 100%; margin-top: 8px;"
                    >
                      <template #icon>
                        <n-icon>
                          <svg viewBox="0 0 24 24">
                            <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
                          </svg>
                        </n-icon>
                      </template>
                      Add Item
                    </n-button>
                  </div>
                </n-form-item>
                <div class="text-editor-actions">
                  <n-button @click="resetTextCopyForm">Reset</n-button>
                  <n-button type="primary" @click="createTextCopy" :loading="isCreatingTextBlock">
                    Create Copy Set
                  </n-button>
                </div>
              </n-form>
            </div>
          </div>

          <!-- Assets Grid (left 2/3 when text type, full width otherwise) -->
          <div class="assets-grid-container" :class="{ 'with-editor': selectedType === 'text-style' || selectedType === 'text-copy', 'with-actions': selectedType === 'bg' || selectedType === 'logo' || selectedType === 'photo' || selectedType === 'reference' }">
            <!-- MOOD: grid of moodboard cards only; click opens board modal -->
            <template v-if="selectedType === 'reference'">
              <div v-if="moodboards.length === 0" class="empty-state">
                <n-empty description="No moodboards yet" />
              </div>
              <div v-else class="assets-grid">
                <div
                  v-for="mb in moodboards"
                  :key="mb.id"
                  class="asset-item"
                >
                  <div
                    class="asset-card moodboard-card"
                    :class="{ selected: selectedMoodboardId === mb.id }"
                    @click="selectMoodboard(mb)"
                  >
                    <div class="asset-preview moodboard-card-preview">
                      <div v-if="getMoodboardThumbnail(mb)" class="moodboard-thumbnail">
                        <img
                          :src="getMoodboardThumbnail(mb)"
                          :alt="mb.name"
                          @error="handleImageError"
                        />
                      </div>
                      <div v-else class="asset-placeholder">
                        <n-icon size="48">
                          <svg viewBox="0 0 24 24">
                            <path fill="currentColor" d="M19 19H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-4.5 8.5l-2.5 3.01L11.5 13 9 16h10l-3.5-4.5z" />
                          </svg>
                        </n-icon>
                      </div>
                      <div class="asset-overlay">
                        <button class="view-button" @click.stop="selectMoodboard(mb)" title="Open moodboard">
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path fill="currentColor" d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
                          </svg>
                        </button>
                        <button class="delete-button" @click.stop="deleteMoodboard(mb)" title="Delete moodboard">
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="asset-info">
                    <h4>{{ mb.name }}</h4>
                    <p class="asset-type">Moodboard</p>
                  </div>
                </div>
              </div>
            </template>

            <!-- Other types: grid of assets -->
            <template v-else>
            <div v-if="filteredAssets.length === 0 && selectedType !== 'bg' && selectedType !== 'logo' && selectedType !== 'photo' && selectedType !== 'text-style' && selectedType !== 'text-copy'" class="empty-state">
              <n-empty description="No assets of this type yet" />
            </div>

            <div v-else class="assets-grid">
              <div
                v-for="asset in filteredAssets"
                :key="asset.id"
                class="asset-item"
              >
                <div class="asset-card" @click="viewAsset(asset)">
                  <div class="asset-preview">
                    <!-- Text Style Preview -->
                    <div v-if="asset.type === 'text-style' && asset.metadata" class="text-style-preview">
                      <div 
                        class="preview-content"
                        :style="getTextStylePreviewStyle(asset)"
                      >
                        {{ getTextStyleSampleText(asset) || 'Sample Text' }}
                      </div>
                    </div>
                    <!-- Text Copy Preview -->
                    <div v-else-if="asset.type === 'text-copy' && asset.metadata" class="text-copy-preview">
                      <div class="preview-content">
                        <div class="copy-preview-label">Text Set:</div>
                        <div class="copy-preview-items">
                          <div 
                            v-for="(item, idx) in getTextCopyItems(asset)" 
                            :key="idx"
                            class="copy-preview-item"
                          >
                            {{ item }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Reference (moodboard) asset: same thumb as first moodboard in MOOD -->
                    <img
                      v-else-if="asset.type === 'reference' && referenceAssetThumbUrl"
                      :src="referenceAssetThumbUrl"
                      alt="Moodboard"
                      @error="handleImageError"
                    />
                    <div v-else-if="asset.type === 'reference'" class="asset-placeholder">
                      <n-icon size="48">
                        <svg viewBox="0 0 24 24">
                          <path fill="currentColor" d="M19 19H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-4.5 8.5l-2.5 3.01L11.5 13 9 16h10l-3.5-4.5z" />
                        </svg>
                      </n-icon>
                    </div>
                    <!-- Color Palette Preview -->
                    <div v-else-if="asset.type === 'colorPalette' && asset.metadata" class="color-palette-preview">
                      <div class="palette-preview-content">
                        <div class="palette-swatches">
                          <div 
                            v-for="(color, idx) in getPaletteColors(asset)" 
                            :key="idx"
                            class="palette-swatch-preview"
                            :style="{ backgroundColor: color.hex }"
                            :title="color.hex"
                          />
                          <div v-if="getPaletteColors(asset).length === 0" class="palette-empty">
                            <span>No colors yet</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Image Preview -->
                    <img
                      v-else-if="asset.metadata && getAssetUrl(asset)"
                      :src="getAssetUrl(asset)"
                      :alt="asset.name"
                      @error="handleImageError"
                    />
                    <div v-else class="asset-placeholder">
                      <n-icon size="48">
                        <svg viewBox="0 0 24 24">
                          <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                      </n-icon>
                    </div>
                  </div>
                  <div class="asset-overlay">
                    <button class="view-button" @click.stop="viewAssetDetails(asset)" title="View details">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path fill="currentColor" d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
                      </svg>
                    </button>
                    <button v-if="asset.type !== 'colorPalette'" class="delete-button" @click.stop="deleteAsset(asset)" title="Delete asset">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="asset-info">
                  <h4>{{ assetDisplayName(asset) }}</h4>
                  <p class="asset-type">{{ assetTypeLabel(asset.type) }}</p>
                  <div v-if="asset.type === 'deliverable' && asset.comp" class="asset-meta">
                    <span>Linked to Comp</span>
                  </div>
                  <div v-if="asset.previousVersion" class="asset-meta">
                    <span>Version</span>
                  </div>
                </div>
              </div>
            </div>
            </template>
          </div>

          <!-- Action Buttons Panel (right side for bg/logo/photo/sticker/reference) -->
          <div v-if="selectedType === 'bg' || selectedType === 'logo' || selectedType === 'photo' || selectedType === 'sticker' || selectedType === 'reference'" class="actions-panel">
            <!-- MOOD: Create moodboard (same position as Create BG / Create Photo) -->
            <template v-if="selectedType === 'reference'">
              <n-button
                type="primary"
                block
                @click="showCreateMoodboardDialog = true"
                :disabled="!selectedProjectId"
                style="margin-bottom: 12px;"
              >
                Create moodboard
              </n-button>
            </template>

            <!-- BG Type Actions -->
            <template v-if="selectedType === 'bg'">
              <n-button
                type="primary"
                block
                @click="openCreateBgModal"
                :disabled="!selectedProjectId"
                style="margin-bottom: 12px;"
              >
                Create BG
              </n-button>
              <n-upload
                v-model:file-list="bgUploadFileList"
                :max="1"
                accept="image/*"
                :show-file-list="false"
                @change="handleBgUploadChange"
                :disabled="!selectedProjectId || isUploadingBg"
              >
                <n-upload-dragger class="actions-upload-dragger">
                  <div class="upload-icon-wrapper">
                    <n-icon size="24" :depth="3">
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                    </n-icon>
                  </div>
                  <n-text style="font-size: 12px">
                    {{ isUploadingBg ? 'Uploading...' : 'Drop or click to upload' }}
                  </n-text>
                </n-upload-dragger>
              </n-upload>
            </template>

            <!-- Logo Type Actions -->
            <template v-if="selectedType === 'logo'">
              <n-upload
                v-model:file-list="logoUploadFileList"
                :max="1"
                accept="image/*"
                :show-file-list="false"
                @change="handleLogoUploadChange"
                :disabled="!selectedProjectId || isUploadingLogo"
              >
                <n-upload-dragger class="actions-upload-dragger">
                  <div class="upload-icon-wrapper">
                    <n-icon size="24" :depth="3">
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                    </n-icon>
                  </div>
                  <n-text style="font-size: 12px">
                    {{ isUploadingLogo ? 'Uploading...' : 'Drop or click to upload' }}
                  </n-text>
                </n-upload-dragger>
              </n-upload>
            </template>

            <!-- Photo Type Actions -->
            <template v-if="selectedType === 'photo'">
              <n-button
                type="primary"
                block
                @click="openCreatePhotoModal"
                :disabled="!selectedProjectId"
                style="margin-bottom: 12px;"
              >
                Create Photo
              </n-button>
              <n-upload
                v-model:file-list="photoUploadFileList"
                :max="1"
                accept="image/*"
                :show-file-list="false"
                @change="handlePhotoUploadChange"
                :disabled="!selectedProjectId || isUploadingPhoto"
              >
                <n-upload-dragger class="actions-upload-dragger">
                  <div class="upload-icon-wrapper">
                    <n-icon size="24" :depth="3">
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                    </n-icon>
                  </div>
                  <n-text style="font-size: 12px">
                    {{ isUploadingPhoto ? 'Uploading...' : 'Drop or click to upload' }}
                  </n-text>
                </n-upload-dragger>
              </n-upload>
            </template>

            <!-- Sticker Type Actions -->
            <template v-if="selectedType === 'sticker'">
              <n-button
                type="primary"
                block
                @click="openCreateStickerModal"
                :disabled="!selectedProjectId"
                style="margin-bottom: 12px;"
              >
                Create Sticker
              </n-button>
              <n-upload
                v-model:file-list="stickerUploadFileList"
                :max="1"
                accept="image/*"
                :show-file-list="false"
                @change="handleStickerUploadChange"
                :disabled="!selectedProjectId || isUploadingSticker"
              >
                <n-upload-dragger class="actions-upload-dragger">
                  <div class="upload-icon-wrapper">
                    <n-icon size="24" :depth="3">
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                    </n-icon>
                  </div>
                  <n-text style="font-size: 12px">
                    {{ isUploadingSticker ? 'Uploading...' : 'Drop or click to upload' }}
                  </n-text>
                </n-upload-dragger>
              </n-upload>
            </template>
          </div>
        </div>
      </div>

      <!-- No Project Selected -->
      <div v-else class="no-project-selected">
        <n-empty description="Please select a project to view assets">
          <template #icon>
            <n-icon size="48">
              <svg viewBox="0 0 24 24">
                <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
            </n-icon>
          </template>
        </n-empty>
      </div>
    </div>

    <!-- Create Asset Dialog -->
    <n-modal v-model:show="showCreateAssetDialog">
      <n-card
        style="width: 600px"
        title="Create Asset"
        :bordered="false"
        size="huge"
        role="dialog"
        aria-modal="true"
      >
        <n-form :model="newAsset" :rules="assetRules" ref="assetFormRef">
          <n-form-item label="Name" path="name">
            <n-input v-model:value="newAsset.name" placeholder="Asset name" />
          </n-form-item>
          <n-form-item label="Type" path="type">
            <n-select
              v-model:value="newAsset.type"
              :options="assetTypeOptions"
              placeholder="Select type (optional)"
              clearable
            />
          </n-form-item>
          <n-form-item label="File" path="file">
            <n-upload
              v-model:file-list="fileList"
              :max="1"
              accept="image/*,application/json,.svg"
              @change="handleFileChange"
            >
              <n-upload-dragger>
                <div style="margin-bottom: 12px">
                  <n-icon size="48" :depth="3">
                    <svg viewBox="0 0 24 24">
                      <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                  </n-icon>
                </div>
                <n-text style="font-size: 16px">Click or drag file to this area to upload</n-text>
              </n-upload-dragger>
            </n-upload>
          </n-form-item>
        </n-form>
        <template #footer>
          <div style="display: flex; justify-content: flex-end; gap: 12px">
            <n-button @click="showCreateAssetDialog = false">Cancel</n-button>
            <n-button type="primary" @click="createAsset" :loading="isCreating">
              Create
            </n-button>
          </div>
        </template>
      </n-card>
    </n-modal>

    <!-- Create Moodboard Dialog -->
    <n-modal v-model:show="showCreateMoodboardDialog">
      <n-card
        style="width: 500px"
        title="Create Moodboard"
        :bordered="false"
        size="huge"
        role="dialog"
        aria-modal="true"
      >
        <n-form :model="newMoodboard" :rules="moodboardRules" ref="moodboardFormRef">
          <n-form-item label="Name" path="name">
            <n-input v-model:value="newMoodboard.name" placeholder="Moodboard name" />
          </n-form-item>
          <n-form-item label="Description" path="description">
            <n-input
              v-model:value="newMoodboard.description"
              type="textarea"
              placeholder="Optional description"
              :rows="3"
            />
          </n-form-item>
        </n-form>
        <template #footer>
          <div style="display: flex; justify-content: flex-end; gap: 12px">
            <n-button @click="showCreateMoodboardDialog = false">Cancel</n-button>
            <n-button type="primary" @click="createMoodboard" :loading="isCreating">
              Create
            </n-button>
          </div>
        </template>
      </n-card>
    </n-modal>

    <!-- Moodboard Board Modal (canvas view) -->
    <MoodboardBoardModal
      v-model:show="showMoodboardBoardModal"
      :moodboard-id="selectedMoodboardId || ''"
      :moodboard-name="selectedMoodboard?.name"
      :images="moodboardImages"
      :canvas-width="selectedMoodboard?.canvasWidth ?? 900"
      :canvas-height="selectedMoodboard?.canvasHeight ?? 600"
      @closed="onMoodboardBoardClosed"
      @deleted="onMoodboardBoardDeleted"
      @layout-saved="onMoodboardLayoutSaved"
      @crop-updated="onMoodboardCropUpdated"
      @name-updated="onMoodboardNameUpdated"
      @font-selected="(fontId) => emit('font-selected', fontId)"
      @word-specimens-updated="onWordSpecimensUpdated"
    />

    <!-- Create BG Modal -->
    <n-modal v-model:show="showCreateBgModal">
      <n-card
        style="width: 900px"
        title="Create Background"
        :bordered="false"
        size="huge"
        role="dialog"
        aria-modal="true"
      >
        <div class="bg-creator-layout">
          <div class="bg-creator-form">
            <n-form-item label="Name & Type">
              <div class="name-type-row">
                <n-input
                  v-model:value="bgForm.name"
                  placeholder="Background name"
                  size="small"
                  style="flex: 1;"
                />
                <n-button-group>
                  <n-button
                    size="small"
                    :type="bgForm.type === 'solid' ? 'primary' : 'default'"
                    @click="bgForm.type = 'solid'; updatePreview()"
                  >
                    Solid
                  </n-button>
                  <n-button
                    size="small"
                    :type="bgForm.type === 'linear' ? 'primary' : 'default'"
                    @click="bgForm.type = 'linear'; updatePreview()"
                  >
                    Linear
                  </n-button>
                  <n-button
                    size="small"
                    :type="bgForm.type === 'radial' ? 'primary' : 'default'"
                    @click="bgForm.type = 'radial'; updatePreview()"
                  >
                    Radial
                  </n-button>
                  <n-button
                    size="small"
                    :type="bgForm.type === 'gen' ? 'primary' : 'default'"
                    @click="bgForm.type = 'gen'"
                  >
                    Gen
                  </n-button>
                </n-button-group>
              </div>
            </n-form-item>

            <n-form-item v-if="bgForm.type !== 'gen'" label="Colors">
              <div class="color-pickers-row" :class="{ 'single-color': bgForm.type === 'solid' }">
                <n-color-picker
                  v-model:value="bgForm.color1"
                  size="small"
                  @update:value="updatePreview()"
                />
                <template v-if="bgForm.type !== 'solid'">
                  <n-button
                    size="small"
                    quaternary
                    @click="swapColors"
                    title="Swap colors"
                    class="swap-button"
                  >
                    <template #icon>
                      <n-icon>
                        <svg viewBox="0 0 24 24">
                          <path fill="currentColor" d="M12,6V9L16,5L12,1V4A8,8 0 0,0 4,12C4,13.57 4.46,15.03 5.24,16.26L6.7,14.8C6.25,13.97 6,13 6,12A6,6 0 0,1 12,6M18.76,7.74L17.3,9.2C17.74,10.04 18,11 18,12A6,6 0 0,1 12,18V15L8,19L12,23V20A8,8 0 0,0 20,12C20,10.43 19.54,8.97 18.76,7.74Z" />
                        </svg>
                      </n-icon>
                    </template>
                  </n-button>
                  <n-color-picker
                    v-model:value="bgForm.color2"
                    size="small"
                    @update:value="updatePreview()"
                  />
                </template>
              </div>
            </n-form-item>

            <n-form-item v-if="bgForm.type !== 'gen'" label="Palette">
              <div class="palette-selector-container">
                <n-select
                  v-model:value="selectedPaletteSource"
                  :options="paletteSourceOptions"
                  placeholder="Select palette"
                  size="small"
                  @update:value="loadPaletteColors"
                />
                <div v-if="paletteColors.length > 0" class="palette-swatches">
                  <div
                    v-for="(color, index) in paletteColors.slice(0, 10)"
                    :key="index"
                    class="palette-swatch"
                    :style="{ backgroundColor: color.hex }"
                    :title="`${color.hex} - Click for Color 1, Option+Click for Color 2`"
                    @click="handlePaletteColorClick($event, color.hex)"
                  ></div>
                </div>
                <n-text v-else-if="!selectedPaletteSource" depth="3" style="font-size: 11px;">
                  Select a palette to view colors
                </n-text>
              </div>
            </n-form-item>

            <!-- Gen Type Form -->
            <template v-if="bgForm.type === 'gen'">
              <n-form-item>
                <template #label>
                  <div class="gen-label-with-icon">
                    <span>General Description</span>
                  </div>
                </template>
                <n-input
                  v-model:value="bgForm.genData.inputs.generalDescription"
                  type="textarea"
                  :rows="3"
                  placeholder="Overall description of the image to generate"
                  size="small"
                />
              </n-form-item>

              <n-form-item>
                <template #label>
                  <div class="gen-label-with-icon">
                    <span>Overall Look</span>
                    <n-button
                      size="tiny"
                      quaternary
                      @click="openImagePicker('overallLook', null, null, 'bg')"
                      title="Select reference image"
                      class="label-image-button"
                    >
                      <template #icon>
                        <n-icon>
                          <svg viewBox="0 0 24 24">
                            <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                          </svg>
                        </n-icon>
                      </template>
                    </n-button>
                  </div>
                </template>
                <n-input
                  v-model:value="bgForm.genData.inputs.overallLook.text"
                  type="textarea"
                  :rows="2"
                  placeholder="e.g., filmic realism, editorial finish"
                  size="small"
                />
              </n-form-item>

              <n-form-item>
                <template #label>
                  <div class="gen-label-with-icon">
                    <span>Framing</span>
                    <n-button
                      size="tiny"
                      quaternary
                      @click="openImagePicker('framing', null, null, 'bg')"
                      title="Select reference image"
                      class="label-image-button"
                    >
                      <template #icon>
                        <n-icon>
                          <svg viewBox="0 0 24 24">
                            <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                          </svg>
                        </n-icon>
                      </template>
                    </n-button>
                  </div>
                </template>
                <n-input
                  v-model:value="bgForm.genData.inputs.framing.text"
                  type="textarea"
                  :rows="2"
                  placeholder="e.g., The bottle and champagne spray should occupy the lower 1/2 of the image with the top 1/2 being mostly background"
                  size="small"
                />
              </n-form-item>

              <n-form-item
                v-for="(obj, objIndex) in bgForm.genData.inputs.objectStyle"
                :key="objIndex"
              >
                <template #label>
                  <div class="gen-label-with-icon" style="display: flex; justify-content: space-between; align-items: center; width: 100%">
                    <div style="display: flex; align-items: center; gap: 8px">
                      <span>Object {{ objIndex + 1 }}</span>
                      <n-button
                        size="tiny"
                        quaternary
                        @click="openImagePicker('objectStyle', objIndex, 0, 'bg')"
                        title="Select reference image"
                        class="label-image-button"
                      >
                        <template #icon>
                          <n-icon>
                            <svg viewBox="0 0 24 24">
                              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                            </svg>
                          </n-icon>
                        </template>
                      </n-button>
                    </div>
                    <n-button
                      size="tiny"
                      type="error"
                      quaternary
                      @click="removeObjectStyle(objIndex, 'bg')"
                      :disabled="bgForm.genData.inputs.objectStyle.length === 1"
                      title="Remove object"
                    >
                      <template #icon>
                        <n-icon>
                          <svg viewBox="0 0 24 24">
                            <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                          </svg>
                        </n-icon>
                      </template>
                    </n-button>
                  </div>
                </template>
                <n-input
                  v-model:value="obj.description"
                  type="textarea"
                  :rows="2"
                  placeholder="e.g., The hero bottle should have gold foil around the neck, and green glass. The bottle should feel traditional and elegant."
                  size="small"
                />
              </n-form-item>
              <n-button
                size="small"
                quaternary
                    @click="addObjectStyle('bg')"
                style="margin-top: 8px"
              >
                + Add Object
              </n-button>

              <n-form-item label="Aspect Ratio">
                <n-select
                  v-model:value="bgForm.genData.inputs.aspectRatio"
                  placeholder="Select aspect ratio"
                  size="small"
                  :options="[
                    { label: '1:1 (Square)', value: '1:1' },
                    { label: '4:3 (Standard)', value: '4:3' },
                    { label: '16:9 (Widescreen)', value: '16:9' },
                    { label: '4:5 (Portrait)', value: '4:5' },
                    { label: '3:2 (Classic)', value: '3:2' },
                    { label: '9:16 (Vertical)', value: '9:16' },
                    { label: '2:3', value: '2:3' },
                    { label: '3:4', value: '3:4' },
                    { label: '5:4', value: '5:4' },
                    { label: '21:9', value: '21:9' }
                  ]"
                />
              </n-form-item>

              <n-form-item label="Avoid">
                <n-input
                  v-model:value="bgForm.genData.inputs.avoid.terms"
                  placeholder="Terms to avoid (comma-separated)"
                  size="small"
                />
              </n-form-item>

              <n-form-item style="margin-top: 16px">
                <template #label>
                  <n-text strong style="font-size: 12px">
                    Generation prompt configuration
                  </n-text>
                </template>
                <n-input
                  :value="constructedPrompt"
                  type="textarea"
                  :rows="8"
                  readonly
                  placeholder="Fill in the form fields above to see the constructed prompt..."
                  size="small"
                  style="font-family: monospace; font-size: 11px; width: 100%"
                />
              </n-form-item>
            </template>
          </div>

          <div v-if="bgForm.type !== 'gen'" class="bg-preview-section">
            <div class="canvas-wrapper">
              <canvas
                ref="bgCanvasRef"
                :width="canvasWidth"
                :height="canvasHeight"
                class="bg-preview-canvas"
                :class="{ 'dragging': isDraggingPoint }"
                @mousedown="handleCanvasMouseDown"
                @mousemove="handleCanvasMouseMove"
                @mouseup="handleCanvasMouseUp"
                @mouseleave="handleCanvasMouseLeave"
              ></canvas>
            </div>
            <div class="canvas-info">
              <n-text depth="3" style="font-size: 12px">
                {{ bgForm.type === 'linear' ? 'Drag start and end points' : bgForm.type === 'radial' ? 'Drag center and edge points' : 'Solid color background' }}
              </n-text>
            </div>
          </div>
        </div>
        <template #footer>
          <div style="display: flex; justify-content: flex-end; gap: 12px">
            <n-button @click="closeCreateBgModal">Cancel</n-button>
            <n-button type="primary" @click="saveBackground" :loading="isCreatingBg" :disabled="!bgForm.name.trim()">
              {{ bgForm.type === 'gen' ? 'Generate' : 'Save' }}
            </n-button>
          </div>
        </template>
      </n-card>
    </n-modal>

    <!-- Create Photo Modal -->
    <n-modal v-model:show="showCreatePhotoModal">
      <n-card
        style="width: 900px"
        title="Create Photo"
        :bordered="false"
        size="huge"
        role="dialog"
        aria-modal="true"
      >
        <div class="bg-creator-layout">
          <div class="bg-creator-form">
            <n-form-item label="Name">
              <n-input
                v-model:value="photoForm.name"
                placeholder="Photo name"
                size="small"
              />
            </n-form-item>

            <!-- Gen Type Form (same as BG) -->
            <n-form-item>
              <template #label>
                <div class="gen-label-with-icon">
                  <span>General Description</span>
                </div>
              </template>
              <n-input
                v-model:value="photoForm.genData.inputs.generalDescription"
                type="textarea"
                :rows="3"
                placeholder="Overall description of the image to generate"
                size="small"
              />
            </n-form-item>

            <n-form-item>
              <template #label>
                <div class="gen-label-with-icon">
                  <span>Overall Look</span>
                  <n-button
                    size="tiny"
                    quaternary
                    @click="openImagePicker('overallLook', null, null, 'photo')"
                    title="Select reference image"
                    class="label-image-button"
                  >
                    <template #icon>
                      <n-icon>
                        <svg viewBox="0 0 24 24">
                          <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                      </n-icon>
                    </template>
                  </n-button>
                </div>
              </template>
              <n-input
                v-model:value="photoForm.genData.inputs.overallLook.text"
                type="textarea"
                :rows="2"
                placeholder="e.g., filmic realism, editorial finish"
                size="small"
              />
            </n-form-item>

            <n-form-item>
              <template #label>
                <div class="gen-label-with-icon">
                  <span>Framing</span>
                  <n-button
                    size="tiny"
                    quaternary
                    @click="openImagePicker('framing', null, null, 'photo')"
                    title="Select reference image"
                    class="label-image-button"
                  >
                    <template #icon>
                      <n-icon>
                        <svg viewBox="0 0 24 24">
                          <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                      </n-icon>
                    </template>
                  </n-button>
                </div>
              </template>
              <n-input
                v-model:value="photoForm.genData.inputs.framing.text"
                type="textarea"
                :rows="2"
                placeholder="e.g., The bottle and champagne spray should occupy the lower 1/2 of the image with the top 1/2 being mostly background"
                size="small"
              />
            </n-form-item>

            <n-form-item
              v-for="(obj, objIndex) in photoForm.genData.inputs.objectStyle"
              :key="objIndex"
            >
              <template #label>
                <div class="gen-label-with-icon" style="display: flex; justify-content: space-between; align-items: center; width: 100%">
                  <div style="display: flex; align-items: center; gap: 8px">
                    <span>Object {{ objIndex + 1 }}</span>
                    <n-button
                      size="tiny"
                      quaternary
                      @click="openImagePicker('objectStyle', objIndex, 0, 'photo')"
                      title="Select reference image"
                      class="label-image-button"
                    >
                      <template #icon>
                        <n-icon>
                          <svg viewBox="0 0 24 24">
                            <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                          </svg>
                        </n-icon>
                      </template>
                    </n-button>
                  </div>
                  <n-button
                    size="tiny"
                    type="error"
                    quaternary
                    @click="removeObjectStyle(objIndex, 'photo')"
                    :disabled="photoForm.genData.inputs.objectStyle.length === 1"
                    title="Remove object"
                  >
                    <template #icon>
                      <n-icon>
                        <svg viewBox="0 0 24 24">
                          <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                        </svg>
                      </n-icon>
                    </template>
                  </n-button>
                </div>
              </template>
              <n-input
                v-model:value="obj.description"
                type="textarea"
                :rows="2"
                placeholder="e.g., The hero bottle should have gold foil around the neck, and green glass. The bottle should feel traditional and elegant."
                size="small"
              />
            </n-form-item>
            <n-button
              size="small"
              quaternary
              @click="addObjectStyle('photo')"
              style="margin-top: 8px"
            >
              + Add Object
            </n-button>

            <n-form-item label="Aspect Ratio">
              <n-select
                v-model:value="photoForm.genData.inputs.aspectRatio"
                placeholder="Select aspect ratio"
                size="small"
                :options="[
                  { label: '1:1 (Square)', value: '1:1' },
                  { label: '4:3 (Standard)', value: '4:3' },
                  { label: '16:9 (Widescreen)', value: '16:9' },
                  { label: '4:5 (Portrait)', value: '4:5' },
                  { label: '3:2 (Classic)', value: '3:2' },
                  { label: '9:16 (Vertical)', value: '9:16' },
                  { label: '2:3', value: '2:3' },
                  { label: '3:4', value: '3:4' },
                  { label: '5:4', value: '5:4' },
                  { label: '21:9', value: '21:9' }
                ]"
              />
            </n-form-item>

            <n-form-item label="Avoid">
              <n-input
                v-model:value="photoForm.genData.inputs.avoid.terms"
                placeholder="Terms to avoid (comma-separated)"
                size="small"
              />
            </n-form-item>

            <n-form-item style="margin-top: 16px">
              <template #label>
                <n-text strong style="font-size: 12px">
                  Generation prompt configuration
                </n-text>
              </template>
              <n-input
                :value="buildPrompt('photo')"
                type="textarea"
                :rows="8"
                readonly
                placeholder="Fill in the form fields above to see the constructed prompt..."
                size="small"
                style="font-family: monospace; font-size: 11px; width: 100%"
              />
            </n-form-item>
          </div>
        </div>
        <template #footer>
          <div style="display: flex; justify-content: flex-end; gap: 12px">
            <n-button @click="closeCreatePhotoModal">Cancel</n-button>
            <n-button type="primary" @click="savePhoto" :loading="isCreatingPhoto" :disabled="!photoForm.name.trim()">
              Generate
            </n-button>
          </div>
        </template>
      </n-card>
    </n-modal>

    <!-- Create Sticker Modal -->
    <n-modal v-model:show="showCreateStickerModal">
      <n-card
        style="width: 900px"
        title="Create Sticker"
        :bordered="false"
        size="huge"
        role="dialog"
        aria-modal="true"
      >
        <div class="bg-creator-layout">
          <div class="bg-creator-form">
            <n-form-item label="Name">
              <n-input
                v-model:value="stickerForm.name"
                placeholder="Sticker name"
                size="small"
              />
            </n-form-item>

            <!-- Gen Type Form (same as BG) -->
            <n-form-item>
              <template #label>
                <div class="gen-label-with-icon">
                  <span>General Description</span>
                </div>
              </template>
              <n-input
                v-model:value="stickerForm.genData.inputs.generalDescription"
                type="textarea"
                :rows="3"
                placeholder="Overall description of the image to generate"
                size="small"
              />
            </n-form-item>

            <n-form-item>
              <template #label>
                <div class="gen-label-with-icon">
                  <span>Overall Look</span>
                  <n-button
                    size="tiny"
                    quaternary
                    @click="openImagePicker('overallLook', null, null, 'sticker')"
                    title="Select reference image"
                    class="label-image-button"
                  >
                    <template #icon>
                      <n-icon>
                        <svg viewBox="0 0 24 24">
                          <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                      </n-icon>
                    </template>
                  </n-button>
                </div>
              </template>
              <n-input
                v-model:value="stickerForm.genData.inputs.overallLook.text"
                type="textarea"
                :rows="2"
                placeholder="e.g., filmic realism, editorial finish"
                size="small"
              />
            </n-form-item>

            <n-form-item>
              <template #label>
                <div class="gen-label-with-icon">
                  <span>Framing</span>
                  <n-button
                    size="tiny"
                    quaternary
                    @click="openImagePicker('framing', null, null, 'sticker')"
                    title="Select reference image"
                    class="label-image-button"
                  >
                    <template #icon>
                      <n-icon>
                        <svg viewBox="0 0 24 24">
                          <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                      </n-icon>
                    </template>
                  </n-button>
                </div>
              </template>
              <n-input
                v-model:value="stickerForm.genData.inputs.framing.text"
                type="textarea"
                :rows="2"
                placeholder="e.g., The bottle and champagne spray should occupy the lower 1/2 of the image with the top 1/2 being mostly background"
                size="small"
              />
            </n-form-item>

            <n-form-item
              v-for="(obj, objIndex) in stickerForm.genData.inputs.objectStyle"
              :key="objIndex"
            >
              <template #label>
                <div class="gen-label-with-icon" style="display: flex; justify-content: space-between; align-items: center; width: 100%">
                  <div style="display: flex; align-items: center; gap: 8px">
                    <span>Object {{ objIndex + 1 }}</span>
                    <n-button
                      size="tiny"
                      quaternary
                      @click="openImagePicker('objectStyle', objIndex, 0, 'sticker')"
                      title="Select reference image"
                      class="label-image-button"
                    >
                      <template #icon>
                        <n-icon>
                          <svg viewBox="0 0 24 24">
                            <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                          </svg>
                        </n-icon>
                      </template>
                    </n-button>
                  </div>
                  <n-button
                    size="tiny"
                    type="error"
                    quaternary
                    @click="removeObjectStyle(objIndex, 'sticker')"
                    :disabled="stickerForm.genData.inputs.objectStyle.length === 1"
                    title="Remove object"
                  >
                    <template #icon>
                      <n-icon>
                        <svg viewBox="0 0 24 24">
                          <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                        </svg>
                      </n-icon>
                    </template>
                  </n-button>
                </div>
              </template>
              <n-input
                v-model:value="obj.description"
                type="textarea"
                :rows="2"
                placeholder="e.g., The hero bottle should have gold foil around the neck, and green glass. The bottle should feel traditional and elegant."
                size="small"
              />
            </n-form-item>
            <n-button
              size="small"
              quaternary
              @click="addObjectStyle('sticker')"
              style="margin-top: 8px"
            >
              + Add Object
            </n-button>

            <n-form-item label="Aspect Ratio">
              <n-select
                v-model:value="stickerForm.genData.inputs.aspectRatio"
                placeholder="Select aspect ratio"
                size="small"
                :options="[
                  { label: '1:1 (Square)', value: '1:1' },
                  { label: '4:3 (Standard)', value: '4:3' },
                  { label: '16:9 (Widescreen)', value: '16:9' },
                  { label: '4:5 (Portrait)', value: '4:5' },
                  { label: '3:2 (Classic)', value: '3:2' },
                  { label: '9:16 (Vertical)', value: '9:16' },
                  { label: '2:3', value: '2:3' },
                  { label: '3:4', value: '3:4' },
                  { label: '5:4', value: '5:4' },
                  { label: '21:9', value: '21:9' }
                ]"
              />
            </n-form-item>

            <n-form-item label="Avoid">
              <n-input
                v-model:value="stickerForm.genData.inputs.avoid.terms"
                placeholder="Terms to avoid (comma-separated)"
                size="small"
              />
            </n-form-item>

            <n-form-item style="margin-top: 16px">
              <template #label>
                <n-text strong style="font-size: 12px">
                  Generation prompt configuration
                </n-text>
              </template>
              <n-input
                :value="buildPrompt('sticker')"
                type="textarea"
                :rows="8"
                readonly
                placeholder="Fill in the form fields above to see the constructed prompt..."
                size="small"
                style="font-family: monospace; font-size: 11px; width: 100%"
              />
            </n-form-item>
          </div>
        </div>
        <template #footer>
          <div style="display: flex; justify-content: flex-end; gap: 12px">
            <n-button @click="closeCreateStickerModal">Cancel</n-button>
            <n-button type="primary" @click="saveSticker" :loading="isCreatingSticker" :disabled="!stickerForm.name.trim()">
              Generate
            </n-button>
          </div>
        </template>
      </n-card>
    </n-modal>

    <!-- Image Picker Modal for Gen Type -->
    <n-modal v-model:show="showImagePickerModal">
      <n-card
        style="width: 800px; max-height: 80vh;"
        title="Select Reference Image"
        :bordered="false"
        size="huge"
        role="dialog"
        aria-modal="true"
      >
        <div v-if="isLoadingImages" class="loading-state">
          <n-spin size="large" />
        </div>
        <div v-else class="image-picker-grid">
          <div
            v-for="image in pickerImages"
            :key="image.id"
            class="image-picker-item"
            @click="selectImageForField(image.id)"
          >
            <div class="image-picker-preview">
              <img
                :src="image.previewUrl || image.iconUrl || image.url"
                :alt="image.basename || image.filename"
                @error="handleImageError"
              />
            </div>
            <p class="image-picker-name">{{ image.basename || image.filename }}</p>
          </div>
        </div>
        <template #footer>
          <div style="display: flex; justify-content: flex-end; gap: 12px">
            <n-button @click="showImagePickerModal = false">Cancel</n-button>
          </div>
        </template>
      </n-card>
    </n-modal>

    <!-- Asset Image Modal -->
    <div v-if="showAssetModal && modalAsset" class="asset-modal-overlay" @click="closeAssetModal">
      <div class="asset-modal" @click.stop>
        <div class="asset-modal-header">
          <button class="asset-modal-close" @click="closeAssetModal" title="Close">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>
        <div class="asset-modal-content">
          <div class="asset-modal-body">
            <!-- Image Section (for non-palette assets) -->
            <div v-if="modalAsset.type !== 'colorPalette'" class="asset-modal-image-section">
              <img
                :src="getFullAssetUrl(modalAsset)"
                :alt="modalAsset.name"
                class="asset-modal-img"
                @error="handleImageError"
              />
            </div>
            <!-- Palette Colors Section (for colorPalette assets) -->
            <div v-else class="asset-modal-palette-section">
              <div class="palette-modal-header">
                <h3>{{ modalAsset.name }}</h3>
                <p class="palette-count">{{ getPaletteColors(modalAsset).length }} color{{ getPaletteColors(modalAsset).length !== 1 ? 's' : '' }}</p>
              </div>
              <div class="palette-modal-swatches">
                <div 
                  v-for="(color, index) in getPaletteColors(modalAsset)" 
                  :key="index"
                  class="palette-modal-swatch"
                  :class="{ 'label-assignment-active': activeLabel !== null }"
                  :style="{ backgroundColor: color.hex }"
                  :title="activeLabel ? `Click to assign to ${activeLabel}` : color.hex"
                  @click="handleSwatchClick(color.hex)"
                >
                  <div class="palette-modal-swatch-overlay">
                    <button 
                      class="palette-swatch-action-button"
                      @click.stop="copyPaletteColor(color.hex)"
                      title="Copy hex to clipboard"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                        <path d="M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z" />
                      </svg>
                    </button>
                    <button 
                      class="palette-swatch-action-button palette-swatch-delete-button"
                      @click.stop="deletePaletteColor(modalAsset.id, index, color.hex)"
                      title="Remove color from palette"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                        <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                      </svg>
                    </button>
                  </div>
                  <div class="palette-modal-swatch-label">{{ color.hex }}</div>
                </div>
                <div v-if="getPaletteColors(modalAsset).length === 0" class="palette-modal-empty">
                  No colors in this palette yet
                </div>
              </div>
            </div>
            <div class="asset-modal-data-section">
              <div class="asset-modal-section">
                <h4 class="asset-modal-section-title">{{ modalAsset.name }}</h4>
                <div class="asset-modal-metadata">
                  <p v-if="modalAsset.type" class="metadata-line">
                    Type: <strong>{{ modalAsset.type }}</strong>
                  </p>
                  <p v-if="modalAsset.width && modalAsset.height" class="metadata-line">
                    Dimensions: {{ modalAsset.width }} × {{ modalAsset.height }}px
                  </p>
                  <p v-if="getAssetMetadata(modalAsset)?.mimeType" class="metadata-line">
                    MIME Type: {{ getAssetMetadata(modalAsset)?.mimeType }}
                  </p>
                  <p v-if="getAssetMetadata(modalAsset)?.size" class="metadata-line">
                    Size: {{ formatFileSize(getAssetMetadata(modalAsset)?.size) }}
                  </p>
                  <p v-if="getAssetMetadata(modalAsset)?.format" class="metadata-line">
                    Format: {{ getAssetMetadata(modalAsset)?.format }}
                  </p>
                  <p v-if="modalAsset.filePath" class="metadata-line">
                    Path: {{ modalAsset.filePath }}
                  </p>
                  <p v-if="modalAsset.createdAt" class="metadata-line">
                    Created: {{ formatDate(modalAsset.createdAt) }}
                  </p>
                </div>
              </div>

              <!-- Actions -->
              <div v-if="modalAsset.type !== 'colorPalette'" class="asset-modal-actions">
                <button 
                  class="asset-modal-action-button" 
                  @click="handleDeleteFromModal" 
                  title="Delete asset"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                  </svg>
                  <span>Delete</span>
                </button>
                <button 
                  class="asset-modal-action-button" 
                  @click="handleOpenInTab" 
                  title="Open in new tab"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path fill="currentColor" d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
                  </svg>
                  <span>Open in Tab</span>
                </button>
              </div>

              <!-- Color Palette Label Assignment -->
              <div v-else class="palette-label-assignment">
                <div class="label-assignment-table">
                  <div
                    v-for="labelName in ['primary', 'secondary', 'accent', 'light', 'dark', 'neutral']"
                    :key="labelName"
                    class="label-assignment-row"
                    :class="{ active: activeLabel === labelName }"
                    @click="activateLabel(labelName)"
                  >
                    <span class="label-name">{{ labelName }}</span>
                    <div
                      class="label-swatch"
                      :style="{ backgroundColor: paletteLabels[labelName] || '#e0e0e0' }"
                    >
                      <span v-if="!paletteLabels[labelName]" class="label-swatch-empty">—</span>
                    </div>
                  </div>
                </div>
                <p v-if="activeLabel" class="label-assignment-hint">
                  Click a color swatch above to assign it to <strong>{{ activeLabel }}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useMessage, NIcon, NSpin, NEmpty, NModal, NCard, NForm, NFormItem, NInput, NInputNumber, NSelect, NButton, NButtonGroup, NUpload, NText, NColorPicker } from 'naive-ui'
import { apiClient } from '@/services/api'
import { fontWoff2Src } from '@/utils/fontUtils'
import MoodboardBoardModal from './MoodboardBoardModal.vue'

// Props
interface Props {
  projectId?: string | null
  selectedProject?: any
}

const props = withDefaults(defineProps<Props>(), {
  projectId: null,
  selectedProject: null
})

// Emits
const emit = defineEmits<{
  'asset-select': [asset: any]
  'font-selected': [fontId: string]
}>()

const message = useMessage()

// Asset types (ALL is default; MOOD shows moodboards; others show typed assets)
const assetTypes = [
  { value: 'all', label: 'ALL' },
  { value: 'reference', label: 'MOOD' },
  { value: 'bg', label: 'BG' },
  { value: 'photo', label: 'PHOTO' },
  { value: 'frame', label: 'FRAME' },
  { value: 'sticker', label: 'STICKER' },
  { value: 'logo', label: 'LOGO' },
  { value: 'text-style', label: 'STYLE' },
  { value: 'text-copy', label: 'COPY' },
  { value: 'colorPalette', label: 'PALETTE' },
  { value: 'src', label: 'SRC' }
] as const

type AssetType = typeof assetTypes[number]['value']

const assetTypeOptions = assetTypes
  .filter(t => t.value !== 'reference' && t.value !== 'all')
  .map(t => ({ label: t.label, value: t.value }))

/** Map asset type value to display label (same as tab labels). Used in ALL view. */
const typeToLabel: Record<string, string> = Object.fromEntries(
  assetTypes.map(t => [t.value, t.label])
)
function assetTypeLabel(type: string | undefined): string {
  if (!type) return 'Unclassified'
  if (type === 'reference' || type === 'reference1') return 'MOOD'
  return typeToLabel[type] ?? type
}

/** Display name for an asset in the grid. For reference (mood) assets, use first moodboard name or "Moodboard" to match MOOD tab. */
function assetDisplayName(asset: { type?: string; name?: string }): string {
  if ((asset.type === 'reference' || asset.type === 'reference1') && moodboards.value.length > 0) {
    return moodboards.value[0].name ?? 'Moodboard'
  }
  if (asset.type === 'reference' || asset.type === 'reference1') return 'Moodboard'
  return asset.name ?? 'Unnamed'
}

// State (default: show all assets)
const selectedType = ref<AssetType>('all')
const assets = ref<any[]>([])
const moodboards = ref<any[]>([])
const selectedMoodboardId = ref<string | null>(null)
const moodboardImages = ref<any[]>([])
const isLoadingMoodboard = ref(false)
/** First moodboard's first image URL; used as thumb for reference asset (ALL) and first moodboard card (MOOD). */
const referenceAssetThumbUrl = ref<string>('')
const isLoading = ref(false)

// Dialogs
const showCreateAssetDialog = ref(false)
const showCreateMoodboardDialog = ref(false)
const showMoodboardBoardModal = ref(false)
const isCreating = ref(false)
const fileList = ref<any[]>([])
const selectedFile = ref<File | null>(null)

// Text Editor
const isCreatingTextBlock = ref(false)
const textStyleFormRef = ref()
const textCopyFormRef = ref()

// Text Style Form
const textStyleForm = ref({
  name: 'H1',
  sampleText: 'Sample Text',
  fontFamily: 'Arial',
  fontSize: 24,
  fontWeight: '400',
  color: '#000000',
  textAlign: 'center' as 'left' | 'center' | 'right'
})

// Text Copy Form
const textCopyForm = ref({
  name: 'Headers',
  items: ['']
})

const fontFamilyOptions = ref<Array<{ label: string; value: string }>>([])

const fontWeightOptions = [
  { label: 'Thin (100)', value: '100' },
  { label: 'Light (300)', value: '300' },
  { label: 'Regular (400)', value: '400' },
  { label: 'Medium (500)', value: '500' },
  { label: 'Semi Bold (600)', value: '600' },
  { label: 'Bold (700)', value: '700' },
  { label: 'Extra Bold (800)', value: '800' },
  { label: 'Black (900)', value: '900' }
]

// BG Upload
const bgUploadFileList = ref<any[]>([])
const isUploadingBg = ref(false)

// BG Creator Modal
const showCreateBgModal = ref(false)
const bgCanvasRef = ref<HTMLCanvasElement | null>(null)
const canvasWidth = 400
const canvasHeight = 400
const isDraggingPoint = ref(false)
const draggedPoint = ref<string | null>(null)
const isCreatingBg = ref(false)
const currentImagePickerField = ref<string | null>(null)
const currentImagePickerObjIndex = ref<number | null>(null)
const currentImagePickerImgIndex = ref<number | null>(null)
const currentImagePickerFormType = ref<'bg' | 'photo' | 'sticker'>('bg')
const showImagePickerModal = ref(false)
const pickerImages = ref<any[]>([])
const isLoadingImages = ref(false)

// BG Form State
const bgForm = ref({
  name: '',
  type: 'solid' as 'solid' | 'linear' | 'radial' | 'gen',
  color1: '#000000',
  color2: '#ffffff',
  // Linear gradient points (0-1 normalized)
  linearStart: { x: 0, y: 0 },
  linearEnd: { x: 1, y: 0 },
  // Radial gradient points (0-1 normalized)
  radialCenter: { x: 0.5, y: 0.5 },
  radialEdge: { x: 0.7, y: 0.5 },
  // Gen type data
  genData: {
    compiler: 'gpt-5.2',
    task: 'generate_images',
    inputs: {
      generalDescription: '',
      overallLook: { fromImageId: null as string | null, text: '' },
      framing: { fromImageId: null as string | null, text: '' },
      objectStyle: [{ description: '', fromImageId: null as string | null }],
      avoid: { terms: '' },
      aspectRatio: ''
    }
  }
})

// Palette state
const selectedPaletteSource = ref<string | null>(null)
const paletteSourceOptions = ref<Array<{ label: string; value: string }>>([])
const paletteColors = ref<Array<{ hex: string }>>([])
const availablePalettes = ref<Array<{ id: string; name: string; type: 'brand' | 'asset' }>>([])

// Logo Upload
const logoUploadFileList = ref<any[]>([])
const isUploadingLogo = ref(false)

// Photo Upload
const photoUploadFileList = ref<any[]>([])
const isUploadingPhoto = ref(false)

// Photo Creator Modal
const showCreatePhotoModal = ref(false)
const isCreatingPhoto = ref(false)

// Photo Form State (same structure as bgForm but for photos)
const photoForm = ref({
  name: '',
  genData: {
    compiler: 'gpt-5.2',
    task: 'generate_images',
    inputs: {
      generalDescription: '',
      overallLook: { fromImageId: null as string | null, text: '' },
      framing: { fromImageId: null as string | null, text: '' },
      objectStyle: [{ description: '', fromImageId: null as string | null }],
      avoid: { terms: '' },
      aspectRatio: ''
    }
  }
})

// Sticker Upload
const stickerUploadFileList = ref<any[]>([])
const isUploadingSticker = ref(false)

// Sticker Creator Modal
const showCreateStickerModal = ref(false)
const isCreatingSticker = ref(false)

// Sticker Form State (same structure as bgForm but for stickers)
const stickerForm = ref({
  name: '',
  genData: {
    compiler: 'gpt-5.2',
    task: 'generate_images',
    inputs: {
      generalDescription: '',
      overallLook: { fromImageId: null as string | null, text: '' },
      framing: { fromImageId: null as string | null, text: '' },
      objectStyle: [{ description: '', fromImageId: null as string | null }],
      avoid: { terms: '' },
      aspectRatio: ''
    }
  }
})

// Forms
const assetFormRef = ref()
const moodboardFormRef = ref()
const newAsset = ref({
  name: '',
  type: null as string | null
})
const newMoodboard = ref({
  name: '',
  description: ''
})

const assetRules = {
  name: {
    required: true,
    message: 'Please enter asset name',
    trigger: 'blur'
  }
}

const moodboardRules = {
  name: {
    required: true,
    message: 'Please enter moodboard name',
    trigger: 'blur'
  }
}

// Computed
const selectedMoodboard = computed(() => {
  if (!selectedMoodboardId.value) return null
  return moodboards.value.find(mb => mb.id === selectedMoodboardId.value) || null
})

const filteredAssets = computed(() => {
  if (selectedType.value === 'all') {
    return assets.value
  }
  if (selectedType.value === 'src') {
    return assets.value.filter(a => !a.type || a.type === '')
  }
  return assets.value.filter(a => a.type === selectedType.value)
})

// Computed refs for project (synced with props)
const selectedProjectId = computed(() => props.projectId)
const selectedProject = computed(() => props.selectedProject)

// Watch for project changes and reload assets
watch(() => props.projectId, (newProjectId, oldProjectId) => {
  if (newProjectId !== oldProjectId) {
    if (newProjectId) {
      loadAssets()
      if (!props.selectedProject) {
        loadProject()
      } else {
        loadFonts()
      }
    } else {
      assets.value = []
      moodboards.value = []
      referenceAssetThumbUrl.value = ''
    }
  }
}, { immediate: true })

watch(() => props.selectedProject, (newProject) => {
  if (newProject) {
    loadFonts()
  }
})

// Load project details
const loadProject = async () => {
  if (!props.projectId) return
  try {
    await apiClient.getProject(props.projectId)
    // Load fonts after project is loaded
    await loadFonts()
  } catch (error) {
    console.error('Failed to load project:', error)
  }
}

// Load fonts
const loadFonts = async () => {
  try {
    const brandId = props.selectedProject?.brandId || props.selectedProject?.brand?.id
    const response = await apiClient.getFonts(brandId)
    
    // Transform fonts to select options format
    fontFamilyOptions.value = response.fonts.map(font => ({
      label: font.displayName,
      value: font.displayName // Using displayName as value to match existing behavior
    }))
  } catch (error) {
    console.error('Failed to load fonts:', error)
    // Fallback to empty array on error
    fontFamilyOptions.value = []
  }
}

// Load assets
const loadAssets = async () => {
  if (!props.projectId) return
  
  isLoading.value = true
  try {
    const response = await apiClient.getAssets({
      projectId: props.projectId,
      limit: 1000
    })
    assets.value = response.assets

    // Always load moodboards when reference asset exists so MOOD tab dropdown is populated
    const referenceAsset = assets.value.find((a: any) => a.type === 'reference')
    if (referenceAsset) {
      const moodboardsResponse = await apiClient.getMoodboards({ assetId: referenceAsset.id })
      moodboards.value = moodboardsResponse.moodboards
    } else {
      moodboards.value = []
    }
    if (moodboards.value.length > 0) {
      loadReferenceAssetThumb()
    } else {
      referenceAssetThumbUrl.value = ''
    }
  } catch (error) {
    console.error('Failed to load assets:', error)
    message.error('Failed to load assets')
  } finally {
    isLoading.value = false
  }
}

/** Load first moodboard's first image URL for reference asset thumb (ALL) and first moodboard card (MOOD). */
async function loadReferenceAssetThumb() {
  if (moodboards.value.length === 0) {
    referenceAssetThumbUrl.value = ''
    return
  }
  const firstMb = moodboards.value[0]
  try {
    const response = await apiClient.getMoodboard(firstMb.id)
    const moodboard = response.moodboard
    if (!moodboard?.items?.length) {
      referenceAssetThumbUrl.value = ''
      return
    }
    const imageItems = moodboard.items.filter((item: any) => item.itemType === 'image')
    if (imageItems.length === 0) {
      referenceAssetThumbUrl.value = ''
      return
    }
    const item = imageItems[0]
    const itemData = typeof item.itemData === 'string' ? JSON.parse(item.itemData) : item.itemData || {}
    const imageId = itemData.imageId || itemData.id || itemData
    if (!imageId) {
      referenceAssetThumbUrl.value = ''
      return
    }
    try {
      const refImagesResponse = await apiClient.getRefImages({ limit: 1000, page: 1 })
      const refImage = refImagesResponse.refImages.find((img: any) => img.id === imageId)
      if (refImage) {
        referenceAssetThumbUrl.value = refImage.previewUrl || refImage.iconUrl || refImage.url || ''
        return
      }
    } catch {
      // fall through to fetchImages
    }
    const imagesResponse = await apiClient.fetchImages({ limit: 1000 })
    const image = imagesResponse.images?.find((img: any) => img.id === imageId)
    if (image) {
      referenceAssetThumbUrl.value = image.previewUrl || image.iconUrl || image.url || ''
    } else {
      referenceAssetThumbUrl.value = ''
    }
  } catch (e) {
    referenceAssetThumbUrl.value = ''
  }
}

/** Default name for the first moodboard when none exist. */
const DEFAULT_MOODBOARD_NAME = 'mood-a'

/** When viewing MOOD and no moodboards exist, create reference asset and default mood-a, then reload. */
const ensureReferenceAndMoodboard = async () => {
  if (!props.projectId) return
  let referenceAsset = assets.value.find((a: any) => a.type === 'reference')
  if (!referenceAsset) {
    try {
      const assetResponse = await apiClient.createAsset({
        projectId: props.projectId,
        type: 'reference',
        name: 'Reference Assets'
      })
      referenceAsset = assetResponse.asset
      assets.value.push(referenceAsset)
    } catch (error) {
      console.error('Failed to create reference asset:', error)
      message.error('Failed to create reference asset')
      return
    }
  }
  if (moodboards.value.length === 0) {
    try {
      const response = await apiClient.createMoodboard({
        assetId: referenceAsset.id,
        name: DEFAULT_MOODBOARD_NAME,
        description: undefined
      })
      moodboards.value = response.moodboard ? [response.moodboard] : []
      if (response.moodboard?.id) {
        selectedMoodboardId.value = response.moodboard.id
        loadMoodboardImages(response.moodboard.id)
      }
    } catch (error) {
      console.error('Failed to create default moodboard:', error)
      message.error('Failed to create default moodboard')
    }
  }
}

// Create asset
const createAsset = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    return
  }

  try {
    await assetFormRef.value?.validate()
  } catch (error) {
    return
  }

  isCreating.value = true
  try {
    if (selectedFile.value) {
      // Upload file
      await apiClient.uploadAsset(
        selectedFile.value,
        selectedProjectId.value,
        {
          type: newAsset.value.type || undefined,
          name: newAsset.value.name
        }
      )
      message.success('Asset created and uploaded')
    } else {
      // Create asset without file
      await apiClient.createAsset({
        projectId: selectedProjectId.value,
        type: newAsset.value.type || undefined,
        name: newAsset.value.name
      })
      message.success('Asset created')
    }

    showCreateAssetDialog.value = false
    newAsset.value = { name: '', type: null }
    fileList.value = []
    selectedFile.value = null
    await loadAssets()
  } catch (error) {
    console.error('Failed to create asset:', error)
    message.error('Failed to create asset')
  } finally {
    isCreating.value = false
  }
}

// Create moodboard
const createMoodboard = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    return
  }

  try {
    await moodboardFormRef.value?.validate()
  } catch (error) {
    return
  }

  // Find or create reference asset
  let referenceAsset = assets.value.find(a => a.type === 'reference')
  if (!referenceAsset) {
    try {
      const assetResponse = await apiClient.createAsset({
        projectId: selectedProjectId.value,
        type: 'reference',
        name: 'Reference Assets'
      })
      referenceAsset = assetResponse.asset
      assets.value.push(referenceAsset)
    } catch (error) {
      message.error('Failed to create reference asset')
      return
    }
  }

  isCreating.value = true
  try {
    const moodboardName = newMoodboard.value.name
    const response = await apiClient.createMoodboard({
      assetId: referenceAsset.id,
      name: moodboardName,
      description: newMoodboard.value.description || undefined
    })
    message.success('Moodboard created')
    showCreateMoodboardDialog.value = false
    newMoodboard.value = { name: '', description: '' }
    await loadAssets()
    if (response.moodboard) {
      selectedMoodboardId.value = response.moodboard.id
      loadMoodboardImages(response.moodboard.id)
    }
  } catch (error) {
    console.error('Failed to create moodboard:', error)
    message.error('Failed to create moodboard')
  } finally {
    isCreating.value = false
  }
}

// File handling
const handleFileChange = (options: { fileList: any[] }) => {
  fileList.value = options.fileList
  if (options.fileList.length > 0) {
    selectedFile.value = options.fileList[0].file as File
  } else {
    selectedFile.value = null
  }
}

function onMoodboardLayoutSaved() {
  if (selectedMoodboardId.value) loadMoodboardImages(selectedMoodboardId.value)
  loadReferenceAssetThumb()
}

function onMoodboardBoardClosed() {
  loadMoodboardImages(selectedMoodboardId.value)
  loadReferenceAssetThumb()
}

function onMoodboardBoardDeleted(itemId: string) {
  // Optimistic removal so the canvas redraws immediately
  moodboardImages.value = moodboardImages.value.filter((img: any) => img.moodboardItemId !== itemId)
  // Then sync with server in case of any edge cases
  loadMoodboardImages(selectedMoodboardId.value)
}

/** Optimistic update when crop is applied/cleared so the board redraws immediately. */
function onMoodboardCropUpdated(payload: { moodboardItemId: string; crop: { cropLeft: number; cropTop: number; cropWidth: number; cropHeight: number } | null }) {
  const idx = moodboardImages.value.findIndex((img: any) => img.moodboardItemId === payload.moodboardItemId)
  if (idx === -1) return
  moodboardImages.value = moodboardImages.value.map((img: any, i) => {
    if (i !== idx) return img
    if (payload.crop) {
      return { ...img, cropLeft: payload.crop.cropLeft, cropTop: payload.crop.cropTop, cropWidth: payload.crop.cropWidth, cropHeight: payload.crop.cropHeight }
    }
    return { ...img, cropLeft: undefined, cropTop: undefined, cropWidth: undefined, cropHeight: undefined }
  })
}

function onMoodboardNameUpdated(newName: string) {
  if (!selectedMoodboardId.value) return
  moodboards.value = moodboards.value.map((mb) =>
    mb.id === selectedMoodboardId.value ? { ...mb, name: newName } : mb
  )
}

function onWordSpecimensUpdated(updates: { moodboardItemId: string; url: string }[]) {
  const urlByItemId = new Map(updates.map((u) => [u.moodboardItemId, u.url]))
  moodboardImages.value = moodboardImages.value.map((img: any) => {
    const url = urlByItemId.get(img.moodboardItemId)
    if (!url) return img
    return { ...img, url, previewUrl: url, iconUrl: url }
  })
}

/** Select a moodboard and open the board/layout modal (no right panel). */
const selectMoodboard = async (mb: { id: string; name: string }) => {
  selectedMoodboardId.value = mb.id
  await loadMoodboardImages(mb.id)
  showMoodboardBoardModal.value = true
}

/** Delete a moodboard after confirmation. */
const deleteMoodboard = async (mb: { id: string; name: string }) => {
  if (!confirm(`Are you sure you want to delete the moodboard "${mb.name}"?`)) {
    return
  }
  try {
    await apiClient.deleteMoodboard(mb.id)
    message.success('Moodboard deleted')
    if (selectedMoodboardId.value === mb.id) {
      selectedMoodboardId.value = null
      showMoodboardBoardModal.value = false
      moodboardImages.value = []
    }
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to delete moodboard:', error)
    message.error(error?.message || 'Failed to delete moodboard')
  }
}

/** Thumbnail URL for a moodboard card. Uses first moodboard's thumb when applicable so ALL and MOOD match. */
const getMoodboardThumbnail = (mb: { id: string }): string | undefined => {
  const firstMb = moodboards.value[0]
  if (firstMb?.id === mb.id && referenceAssetThumbUrl.value) {
    return referenceAssetThumbUrl.value
  }
  if (selectedMoodboardId.value !== mb.id || moodboardImages.value.length === 0) return undefined
  const first = moodboardImages.value[0]
  return first.previewUrl || first.iconUrl || first.url
}

// Load moodboard images
const loadMoodboardImages = async (moodboardId: string | null) => {
  if (!moodboardId) {
    moodboardImages.value = []
    return
  }

  isLoadingMoodboard.value = true
  try {
    const response = await apiClient.getMoodboard(moodboardId)
    const moodboard = response.moodboard
    
    if (!moodboard.items || moodboard.items.length === 0) {
      moodboardImages.value = []
      isLoadingMoodboard.value = false
      return
    }

    // Extract image items and fetch image data
    const imageItems = moodboard.items.filter((item: any) => item.itemType === 'image')
    const fontItems = moodboard.items.filter((item: any) => item.itemType === 'font' && item.font)

    if (imageItems.length === 0 && fontItems.length === 0) {
      moodboardImages.value = []
      isLoadingMoodboard.value = false
      return
    }

    // Parse itemData to get image IDs and fetch images
    const imagePromises = imageItems.map(async (item: any) => {
      try {
        // Parse itemData to get imageId
        const itemData = typeof item.itemData === 'string' ? JSON.parse(item.itemData) : item.itemData
        const imageId = itemData.imageId || itemData.id || itemData
        
        if (!imageId) {
          return null
        }

        // Try to fetch from refImages API first (most likely for moodboards)
        try {
          const refImagesResponse = await apiClient.getRefImages({ 
            limit: 1000,
            page: 1 
          })
          const refImage = refImagesResponse.refImages.find((img: any) => img.id === imageId)
          if (refImage) {
            return {
              id: refImage.id,
              moodboardItemId: item.id,
              itemData: item.itemData,
              starred: (itemData.starred === true),
              rating: refImage.rating ?? undefined,
              order: item.order ?? undefined,
              pageNumber: item.pageNumber ?? 1,
              positionX: item.positionX ?? undefined,
              positionY: item.positionY ?? undefined,
              scale: item.scale ?? undefined,
              cropLeft: item.cropLeft ?? undefined,
              cropTop: item.cropTop ?? undefined,
              cropWidth: item.cropWidth ?? undefined,
              cropHeight: item.cropHeight ?? undefined,
              filename: refImage.basename,
              url: refImage.url,
              previewUrl: refImage.previewUrl,
              iconUrl: refImage.iconUrl,
              width: refImage.width,
              height: refImage.height
            }
          }
        } catch (e) {
          // If refImages doesn't work, try regular images
        }

        // Try to fetch from regular images API
        try {
          const imagesResponse = await apiClient.fetchImages({ limit: 1000 })
          const image = imagesResponse.images.find((img: any) => img.id === imageId)
          if (image) {
            return {
              id: image.id,
              moodboardItemId: item.id,
              itemData: item.itemData,
              starred: (itemData.starred === true),
              rating: image.rating ?? undefined,
              order: item.order ?? undefined,
              pageNumber: item.pageNumber ?? 1,
              positionX: item.positionX ?? undefined,
              positionY: item.positionY ?? undefined,
              scale: item.scale ?? undefined,
              cropLeft: item.cropLeft ?? undefined,
              cropTop: item.cropTop ?? undefined,
              cropWidth: item.cropWidth ?? undefined,
              cropHeight: item.cropHeight ?? undefined,
              filename: image.filename,
              url: image.url,
              previewUrl: image.previewUrl,
              iconUrl: image.iconUrl,
              width: image.width,
              height: image.height
            }
          }
        } catch (e) {
          console.error('Error fetching image:', e)
        }

        return null
      } catch (error) {
        console.error('Error parsing moodboard item:', error)
        return null
      }
    })
    
    const resolved = await Promise.all(imagePromises)

    // Build font item entries — woff2Url enables live canvas text rendering
    const resolvedFontItems = fontItems.map((item: any) => {
      const font = item.font
      let word: string | undefined
      try { word = JSON.parse(item.itemData)?.word } catch { /* ignore */ }
      return {
        id: `font-${font.id}`,
        moodboardItemId: item.id,
        itemData: item.itemData,
        isFontItem: true,
        fontId: font.id,
        fontName: font.displayName,
        woff2Url: fontWoff2Src(font),
        word,
        starred: false,
        rating: undefined,
        order: item.order ?? undefined,
        pageNumber: 1,
        positionX: item.positionX ?? undefined,
        positionY: item.positionY ?? undefined,
        scale: item.scale ?? undefined,
        url: font.thumbnailUrl || null,
        previewUrl: font.thumbnailUrl || null,
        iconUrl: font.thumbnailUrl || null,
        width: 1600,
        height: 900
      }
    })

    // Dedupe by moodboardItemId (same API item once) and by image id (same image once in layout)
    const byImageId = new Map<string, any>()
    for (const img of resolved) {
      if (img == null) continue
      if (byImageId.has(img.id)) continue
      byImageId.set(img.id, img)
    }
    for (const fontEntry of resolvedFontItems) {
      byImageId.set(fontEntry.id, fontEntry)
    }
    moodboardImages.value = Array.from(byImageId.values())
  } catch (error) {
    console.error('Failed to load moodboard images:', error)
    message.error('Failed to load moodboard images')
    moodboardImages.value = []
  } finally {
    isLoadingMoodboard.value = false
  }
}

// Load text style asset into form
const loadTextStyleIntoForm = (asset: any) => {
  if (asset.type !== 'text-style') return
  
  const meta = getAssetMetadata(asset)
  textStyleForm.value = {
    name: asset.name || 'H1',
    sampleText: meta.sampleText || 'Sample Text',
    fontFamily: meta.fontFamily || 'Arial',
    fontSize: meta.fontSize || 24,
    fontWeight: meta.fontWeight || '400',
    color: meta.color || '#000000',
    textAlign: (meta.textAlign || 'center') as 'left' | 'center' | 'right'
  }
  
  message.success(`Loaded "${asset.name}" into form`)
}

// Load text copy asset into form
const loadTextCopyIntoForm = (asset: any) => {
  if (asset.type !== 'text-copy') return
  
  const meta = getAssetMetadata(asset)
  const items = meta.items || []
  textCopyForm.value = {
    name: asset.name || 'Headers',
    items: items.length > 0 ? [...items] : ['']
  }
  
  message.success(`Loaded "${asset.name}" into form`)
}

// View asset/moodboard
const viewAsset = (asset: any) => {
  // Emit asset-select event for parent components
  emit('asset-select', asset)
  
  // For text-style and text-copy assets, load into form instead of opening modal
  if (asset.type === 'text-style' && selectedType.value === 'text-style') {
    loadTextStyleIntoForm(asset)
    return
  }
  if (asset.type === 'text-copy' && selectedType.value === 'text-copy') {
    loadTextCopyIntoForm(asset)
    return
  }
  
  // For other asset types, open modal as before
  viewAssetDetails(asset)
}

const showAssetModal = ref(false)
const modalAsset = ref<any>(null)

// Color palette label assignment state
const activeLabel = ref<string | null>(null)
const paletteLabels = ref<Record<string, string | undefined>>({
  primary: undefined,
  secondary: undefined,
  accent: undefined,
  light: undefined,
  dark: undefined,
  neutral: undefined
})

/** Open the moodboard board modal for the reference asset (e.g. from ALL view). Uses first moodboard. */
const openMoodboardModalForReference = async (referenceAsset: { id: string }) => {
  try {
    const moodboardsResponse = await apiClient.getMoodboards({ assetId: referenceAsset.id })
    const list = moodboardsResponse.moodboards || []
    if (list.length === 0) {
      message.warning('No moodboards yet. Create one from the MOOD tab.')
      return
    }
    const first = list[0]
    selectedMoodboardId.value = first.id
    moodboards.value = list
    await loadMoodboardImages(first.id)
    showMoodboardBoardModal.value = true
  } catch (error) {
    console.error('Failed to open moodboard:', error)
    message.error('Failed to load moodboards')
  }
}

const viewAssetDetails = async (asset: any) => {
  // For text-style and text-copy assets, load into form instead of opening modal
  if (asset.type === 'text-style' && selectedType.value === 'text-style') {
    loadTextStyleIntoForm(asset)
    return
  }
  if (asset.type === 'text-copy' && selectedType.value === 'text-copy') {
    loadTextCopyIntoForm(asset)
    return
  }

  // Reference asset: open moodboard board modal (first moodboard) instead of asset detail modal
  if (asset.type === 'reference') {
    await openMoodboardModalForReference(asset)
    return
  }
  
  // For other asset types, open asset detail modal
  modalAsset.value = asset
  showAssetModal.value = true
  
  if (asset.type === 'colorPalette') {
    paletteLabels.value = getPaletteLabels(asset)
    activeLabel.value = null
  }
}

const closeAssetModal = () => {
  showAssetModal.value = false
  modalAsset.value = null
}

const getFullAssetUrl = (asset: any): string | undefined => {
  try {
    if (asset.metadata) {
      const meta = JSON.parse(asset.metadata)
      // Use full URL (not preview/icon) for modal
      return meta.url || undefined
    }
  } catch (e) {
    // Ignore parse errors
  }
  return undefined
}

const getAssetMetadata = (asset: any): any => {
  try {
    if (asset.metadata) {
      return JSON.parse(asset.metadata)
    }
  } catch (e) {
    // Ignore parse errors
  }
  return {}
}

// Text Style Preview Helpers
const getTextStylePreviewStyle = (asset: any): Record<string, string> => {
  const meta = getAssetMetadata(asset)
  return {
    fontFamily: meta.fontFamily || 'Arial',
    fontSize: `${meta.fontSize || 24}px`,
    fontWeight: meta.fontWeight || '400',
    color: meta.color || '#000000',
    textAlign: meta.textAlign || 'center'
  }
}

const getTextStyleSampleText = (asset: any): string => {
  const meta = getAssetMetadata(asset)
  return meta.sampleText || 'Sample Text'
}

// Text Copy Preview Helpers
const getTextCopyItems = (asset: any): string[] => {
  const meta = getAssetMetadata(asset)
  return meta.items || []
}

// Color Palette Preview Helpers
const getPaletteColors = (asset: any): Array<{ hex: string }> => {
  const meta = getAssetMetadata(asset)
  return meta.colors || []
}

// Get palette labels from metadata
const getPaletteLabels = (asset: any): Record<string, string | undefined> => {
  const meta = getAssetMetadata(asset)
  return meta.labels || {
    primary: undefined,
    secondary: undefined,
    accent: undefined,
    light: undefined,
    dark: undefined,
    neutral: undefined
  }
}

// Activate a label for assignment
const activateLabel = (labelName: string) => {
  activeLabel.value = labelName
}

// Clear active label
const clearActiveLabel = () => {
  activeLabel.value = null
}

// Assign color to active label
const assignColorToLabel = async (labelName: string, hex: string) => {
  if (!modalAsset.value || modalAsset.value.type !== 'colorPalette') {
    return
  }

  try {
    // Update local state immediately
    paletteLabels.value[labelName] = hex
    
    // Save to backend
    await apiClient.updatePaletteLabels(modalAsset.value.id, {
      ...paletteLabels.value
    })
    
    // Reload modal asset to get updated metadata
    const updated = await apiClient.getAsset(modalAsset.value.id)
    modalAsset.value = updated.asset
    
    // Update labels from fresh metadata
    paletteLabels.value = getPaletteLabels(updated.asset)
    
    message.success(`Assigned ${hex} to ${labelName}`)
    clearActiveLabel()
  } catch (error: any) {
    console.error('Failed to assign color to label:', error)
    message.error(error?.message || 'Failed to assign color to label')
    // Revert local state on error
    const meta = getAssetMetadata(modalAsset.value)
    paletteLabels.value = meta.labels || {
      primary: undefined,
      secondary: undefined,
      accent: undefined,
      light: undefined,
      dark: undefined,
      neutral: undefined
    }
  }
}

// Handle swatch click - either assign to label or copy
const handleSwatchClick = (hex: string) => {
  if (activeLabel.value) {
    // In label assignment mode
    assignColorToLabel(activeLabel.value, hex)
  }
  // Otherwise, do nothing (overlay buttons handle copy/delete)
}

// Copy hex color to clipboard
const copyPaletteColor = async (hex: string) => {
  if (!hex) {
    message.error('No HEX value to copy')
    return
  }
  try {
    await navigator.clipboard.writeText(hex)
    message.success(`Copied ${hex} to clipboard`)
  } catch (error) {
    message.error('Failed to copy to clipboard')
  }
}

// Delete color from palette
const deletePaletteColor = async (assetId: string, colorIndex: number, hex: string) => {
  if (!confirm(`Remove color ${hex} from palette?`)) {
    return
  }
  
  try {
    await apiClient.deletePaletteColor(assetId, colorIndex)
    message.success(`Removed ${hex} from palette`)
    // Reload the modal asset if it's the same one being viewed
    if (modalAsset.value && modalAsset.value.id === assetId) {
      const updated = await apiClient.getAsset(assetId)
      modalAsset.value = updated.asset
    }
    // Also reload assets list to keep everything in sync
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to delete color from palette:', error)
    message.error(error?.response?.data?.message || 'Failed to remove color from palette')
  }
}

const formatFileSize = (bytes: number): string => {
  if (!bytes) return 'Unknown'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

const formatDate = (date: string | Date): string => {
  if (!date) return 'Unknown'
  try {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString()
  } catch (e) {
    return 'Invalid date'
  }
}

const handleDeleteFromModal = async () => {
  if (!modalAsset.value) return
  await deleteAsset(modalAsset.value)
  closeAssetModal()
}

const handleOpenInTab = () => {
  if (!modalAsset.value) return
  const url = getFullAssetUrl(modalAsset.value)
  if (url) {
    window.open(url, '_blank')
  }
}

// Handle Escape key to close modal
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && showAssetModal.value) {
    closeAssetModal()
  }
}

// Add/remove keyboard listener when modal opens/closes
watch(showAssetModal, (isOpen) => {
  if (isOpen) {
    window.addEventListener('keydown', handleEscapeKey)
  } else {
    window.removeEventListener('keydown', handleEscapeKey)
  }
})

const deleteAsset = async (asset: any) => {
  if (!confirm(`Are you sure you want to delete "${asset.name}"?`)) {
    return
  }
  
  try {
    await apiClient.deleteAsset(asset.id)
    message.success('Asset deleted successfully')
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to delete asset:', error)
    message.error(error?.message || 'Failed to delete asset')
  }
}

// Helper functions
const getAssetUrl = (asset: any): string | undefined => {
  try {
    if (asset.metadata) {
      const meta = JSON.parse(asset.metadata)
      // Prefer previewUrl (thumbnail) for display, fall back to iconUrl, then url
      return meta.previewUrl || meta.iconUrl || meta.url || undefined
    }
  } catch (e) {
    // Ignore parse errors
  }
  return undefined
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWxsPSIjZjVmNWY1IiBzdHJva2U9IiNkMWQ1ZGIiLz4KPHRleHQgeD0iNjAiIHk9IjYwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTIiPkltYWdlPC90ZXh0Pgo8dGV4dCB4PSI2MCIgeT0iNzUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjNjY2IiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMCI+Tm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K'
}

// Handle BG upload
const handleBgUploadChange = async (options: { fileList: any[] }) => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    bgUploadFileList.value = []
    return
  }

  const fileList = options.fileList
  if (fileList.length === 0) return

  const file = fileList[0].file as File
  if (!file) return

  isUploadingBg.value = true
  try {
    await apiClient.uploadAsset(
      file,
      selectedProjectId.value,
      {
        type: 'bg',
        name: file.name.replace(/\.[^/.]+$/, '') // Remove extension for name
      }
    )
    
    message.success('BG asset uploaded successfully')
    bgUploadFileList.value = []
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to upload BG asset:', error)
    message.error(error?.message || 'Failed to upload BG asset')
    bgUploadFileList.value = []
  } finally {
    isUploadingBg.value = false
  }
}

// Handle Logo upload
const handleLogoUploadChange = async (options: { fileList: any[] }) => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    logoUploadFileList.value = []
    return
  }

  const fileList = options.fileList
  if (fileList.length === 0) return

  const file = fileList[0].file as File
  if (!file) return

  isUploadingLogo.value = true
  try {
    await apiClient.uploadAsset(
      file,
      selectedProjectId.value,
      {
        type: 'logo',
        name: file.name.replace(/\.[^/.]+$/, '') // Remove extension for name
      }
    )
    
    message.success('Logo asset uploaded successfully')
    logoUploadFileList.value = []
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to upload Logo asset:', error)
    message.error(error?.message || 'Failed to upload Logo asset')
    logoUploadFileList.value = []
  } finally {
    isUploadingLogo.value = false
  }
}

// Handle Photo upload
const handlePhotoUploadChange = async (options: { fileList: any[] }) => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    photoUploadFileList.value = []
    return
  }

  const fileList = options.fileList
  if (fileList.length === 0) return

  const file = fileList[0].file as File
  if (!file) return

  isUploadingPhoto.value = true
  try {
    await apiClient.uploadAsset(
      file,
      selectedProjectId.value,
      {
        type: 'photo',
        name: file.name.replace(/\.[^/.]+$/, '') // Remove extension for name
      }
    )
    
    message.success('Photo asset uploaded successfully')
    photoUploadFileList.value = []
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to upload Photo asset:', error)
    message.error(error?.message || 'Failed to upload Photo asset')
    photoUploadFileList.value = []
  } finally {
    isUploadingPhoto.value = false
  }
}

// Handle Sticker upload
const handleStickerUploadChange = async (options: { fileList: any[] }) => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    stickerUploadFileList.value = []
    return
  }

  const fileList = options.fileList
  if (fileList.length === 0) return

  const file = fileList[0].file as File
  if (!file) return

  isUploadingSticker.value = true
  try {
    await apiClient.uploadAsset(
      file,
      selectedProjectId.value,
      {
        type: 'sticker',
        name: file.name.replace(/\.[^/.]+$/, '') // Remove extension for name
      }
    )
    
    message.success('Sticker asset uploaded successfully')
    stickerUploadFileList.value = []
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to upload Sticker asset:', error)
    message.error(error?.message || 'Failed to upload Sticker asset')
    stickerUploadFileList.value = []
  } finally {
    isUploadingSticker.value = false
  }
}

// BG Creator Functions
const openCreateBgModal = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    return
  }
  resetBgForm()
  await loadAvailablePalettes()
  // Generate default name
  bgForm.value.name = generateDefaultBgName()
  showCreateBgModal.value = true
  // Wait for next tick to ensure canvas is rendered
  setTimeout(() => {
    updatePreview()
  }, 100)
}

const generateDefaultBgName = (): string => {
  if (!selectedProjectId.value) return 'bg_a'
  
  // Get all existing bg assets
  const bgAssets = assets.value.filter(a => a.type === 'bg')
  
  // Extract existing names that match pattern bg_X where X is a single letter
  const existingNames = new Set(
    bgAssets
      .map(a => a.name.toLowerCase())
      .filter(name => /^bg_[a-z]$/.test(name))
  )
  
  // Find the next available letter
  const letters = 'abcdefghijklmnopqrstuvwxyz'
  for (let i = 0; i < letters.length; i++) {
    const candidate = `bg_${letters[i]}`
    if (!existingNames.has(candidate)) {
      return candidate
    }
  }
  
  // If all single letters are taken, try bg_aa, bg_ab, etc.
  for (let i = 0; i < letters.length; i++) {
    for (let j = 0; j < letters.length; j++) {
      const candidate = `bg_${letters[i]}${letters[j]}`
      if (!bgAssets.some(a => a.name.toLowerCase() === candidate)) {
        return candidate
      }
    }
  }
  
  // Fallback
  return `bg_${Date.now()}`
}

const loadAvailablePalettes = async () => {
  if (!selectedProjectId.value) return

  availablePalettes.value = []
  paletteSourceOptions.value = []

  try {
    // Ensure project is loaded
    if (!selectedProject.value) {
      await loadProject()
    }

    // Add brand palette option
    if (selectedProject.value?.brandId) {
      try {
        const brand = await apiClient.getBrand(selectedProject.value.brandId)
        if (brand.brand?.colors) {
          availablePalettes.value.push({
            id: 'brand',
            name: 'Brand Colors',
            type: 'brand'
          })
        }
      } catch (error) {
        console.error('Failed to load brand:', error)
      }
    }

    // Add colorPalette assets
    try {
      const response = await apiClient.getColorPalettes(selectedProjectId.value)
      response.assets.forEach((asset: any) => {
        availablePalettes.value.push({
          id: asset.id,
          name: asset.name,
          type: 'asset'
        })
      })
    } catch (error) {
      console.error('Failed to load color palettes:', error)
    }

    // Build options for dropdown
    paletteSourceOptions.value = availablePalettes.value.map(p => ({
      label: p.name,
      value: p.id
    }))

    // Auto-select brand if available, otherwise first palette
    if (availablePalettes.value.length > 0) {
      selectedPaletteSource.value = availablePalettes.value[0].id
      await loadPaletteColors()
    }
  } catch (error) {
    console.error('Failed to load palettes:', error)
  }
}

const loadPaletteColors = async () => {
  if (!selectedPaletteSource.value) {
    paletteColors.value = []
    return
  }

  try {
    const palette = availablePalettes.value.find(p => p.id === selectedPaletteSource.value)
    if (!palette) {
      paletteColors.value = []
      return
    }

    if (palette.type === 'brand') {
      // Load brand colors
      if (!selectedProject.value?.brandId) return
      const brand = await apiClient.getBrand(selectedProject.value.brandId)
      if (brand.brand?.colors) {
        const colorsData = typeof brand.brand.colors === 'string'
          ? JSON.parse(brand.brand.colors)
          : brand.brand.colors

        const allColors: Array<{ hex: string }> = []
        if (Array.isArray(colorsData)) {
          colorsData.forEach((palette: any) => {
            if (palette.colors && Array.isArray(palette.colors)) {
              palette.colors.forEach((color: any) => {
                if (color.hex) {
                  allColors.push({ hex: color.hex })
                }
              })
            }
          })
        }
        paletteColors.value = allColors
      }
    } else {
      // Load palette asset colors
      const response = await apiClient.getPaletteColors(palette.id)
      paletteColors.value = response.colors.map((c: any) => ({ hex: c.hex }))
    }
  } catch (error) {
    console.error('Failed to load palette colors:', error)
    paletteColors.value = []
  }
}

const swapColors = () => {
  const temp = bgForm.value.color1
  bgForm.value.color1 = bgForm.value.color2
  bgForm.value.color2 = temp
  updatePreview()
}

const handlePaletteColorClick = (event: MouseEvent, hex: string) => {
  // Check for Alt/Option key (Mac) or Alt key (Windows/Linux)
  if (event.altKey || event.metaKey) {
    // Option/Alt click - set color2
    if (bgForm.value.type !== 'solid') {
      bgForm.value.color2 = hex
      updatePreview()
    }
  } else {
    // Regular click - set color1
    bgForm.value.color1 = hex
    updatePreview()
  }
}

const closeCreateBgModal = () => {
  showCreateBgModal.value = false
  resetBgForm()
}

// Photo Creator Functions
const openCreatePhotoModal = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    return
  }
  resetPhotoForm()
  photoForm.value.name = generateDefaultPhotoName()
  showCreatePhotoModal.value = true
}

const generateDefaultPhotoName = (): string => {
  if (!selectedProjectId.value) return 'photo_a'
  
  const photoAssets = assets.value.filter(a => a.type === 'photo')
  const existingNames = new Set(
    photoAssets
      .map(a => a.name.toLowerCase())
      .filter(name => /^photo_[a-z]$/.test(name))
  )
  
  const letters = 'abcdefghijklmnopqrstuvwxyz'
  for (let i = 0; i < letters.length; i++) {
    const candidate = `photo_${letters[i]}`
    if (!existingNames.has(candidate)) {
      return candidate
    }
  }
  
  return `photo_${Date.now()}`
}

const closeCreatePhotoModal = () => {
  showCreatePhotoModal.value = false
  resetPhotoForm()
}

const resetPhotoForm = () => {
  photoForm.value = {
    name: '',
    genData: {
      compiler: 'gpt-5.2',
      task: 'generate_images',
      inputs: {
        generalDescription: '',
        overallLook: { fromImageId: null, text: '' },
        framing: { fromImageId: null, text: '' },
        objectStyle: [{ description: '', fromImageId: null }],
        avoid: { terms: '' },
        aspectRatio: ''
      }
    }
  }
}

// Sticker Creator Functions
const openCreateStickerModal = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    return
  }
  resetStickerForm()
  stickerForm.value.name = generateDefaultStickerName()
  showCreateStickerModal.value = true
}

const generateDefaultStickerName = (): string => {
  if (!selectedProjectId.value) return 'sticker_a'
  
  const stickerAssets = assets.value.filter(a => a.type === 'sticker')
  const existingNames = new Set(
    stickerAssets
      .map(a => a.name.toLowerCase())
      .filter(name => /^sticker_[a-z]$/.test(name))
  )
  
  const letters = 'abcdefghijklmnopqrstuvwxyz'
  for (let i = 0; i < letters.length; i++) {
    const candidate = `sticker_${letters[i]}`
    if (!existingNames.has(candidate)) {
      return candidate
    }
  }
  
  return `sticker_${Date.now()}`
}

const closeCreateStickerModal = () => {
  showCreateStickerModal.value = false
  resetStickerForm()
}

const resetStickerForm = () => {
  stickerForm.value = {
    name: '',
    genData: {
      compiler: 'gpt-5.2',
      task: 'generate_images',
      inputs: {
        generalDescription: '',
        overallLook: { fromImageId: null, text: '' },
        framing: { fromImageId: null, text: '' },
        objectStyle: [{ description: '', fromImageId: null }],
        avoid: { terms: '' },
        aspectRatio: ''
      }
    }
  }
}

const resetBgForm = () => {
  bgForm.value = {
    name: '',
    type: 'solid',
    color1: '#000000',
    color2: '#ffffff',
    linearStart: { x: 0, y: 0 },
    linearEnd: { x: 1, y: 0 },
    radialCenter: { x: 0.5, y: 0.5 },
    radialEdge: { x: 0.7, y: 0.5 },
    genData: {
      compiler: 'gpt-5.2',
      task: 'generate_images',
      inputs: {
        generalDescription: '',
        overallLook: { fromImageId: null, text: '' },
        framing: { fromImageId: null, text: '' },
        objectStyle: [{ description: '', fromImageId: null }],
        avoid: { terms: '' },
        aspectRatio: ''
      }
    }
  }
  isDraggingPoint.value = false
  draggedPoint.value = null
  selectedPaletteSource.value = null
  paletteColors.value = []
  availablePalettes.value = []
  paletteSourceOptions.value = []
  currentImagePickerField.value = null
  currentImagePickerObjIndex.value = null
  currentImagePickerImgIndex.value = null
  showImagePickerModal.value = false
  pickerImages.value = []
}

const updatePreview = () => {
  if (!bgCanvasRef.value) return
  
  const canvas = bgCanvasRef.value
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  renderGradient(ctx, canvas.width, canvas.height, true)
}

const renderGradient = (ctx: CanvasRenderingContext2D, width: number, height: number, showPoints: boolean = false) => {
  const { type, color1, color2, linearStart, linearEnd, radialCenter, radialEdge } = bgForm.value

  if (type === 'solid') {
    ctx.fillStyle = color1
    ctx.fillRect(0, 0, width, height)
  } else if (type === 'linear') {
    const startX = linearStart.x * width
    const startY = linearStart.y * height
    const endX = linearEnd.x * width
    const endY = linearEnd.y * height
    
    const gradient = ctx.createLinearGradient(startX, startY, endX, endY)
    gradient.addColorStop(0, color1)
    gradient.addColorStop(1, color2)
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)

    if (showPoints) {
      // Draw start point
      ctx.fillStyle = '#ffffff'
      ctx.strokeStyle = '#000000'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(startX, startY, 8, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()

      // Draw end point
      ctx.beginPath()
      ctx.arc(endX, endY, 8, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    }
  } else if (type === 'radial') {
    const centerX = radialCenter.x * width
    const centerY = radialCenter.y * height
    const edgeX = radialEdge.x * width
    const edgeY = radialEdge.y * height
    
    const radius = Math.sqrt(
      Math.pow(edgeX - centerX, 2) + Math.pow(edgeY - centerY, 2)
    )
    
    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius)
    gradient.addColorStop(0, color1)
    gradient.addColorStop(1, color2)
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)

    if (showPoints) {
      // Draw center point
      ctx.fillStyle = '#ffffff'
      ctx.strokeStyle = '#000000'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(centerX, centerY, 8, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()

      // Draw edge point
      ctx.beginPath()
      ctx.arc(edgeX, edgeY, 8, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()

      // Draw line from center to edge
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(centerX, centerY)
      ctx.lineTo(edgeX, edgeY)
      ctx.stroke()
    }
  }
}

const getPointAtPosition = (x: number, y: number): string | null => {
  const canvas = bgCanvasRef.value
  if (!canvas) return null

  const rect = canvas.getBoundingClientRect()
  const canvasX = x - rect.left
  const canvasY = y - rect.top

  const threshold = 15 // pixels

  if (bgForm.value.type === 'linear') {
    const startX = bgForm.value.linearStart.x * canvas.width
    const startY = bgForm.value.linearStart.y * canvas.height
    const endX = bgForm.value.linearEnd.x * canvas.width
    const endY = bgForm.value.linearEnd.y * canvas.height

    const distToStart = Math.sqrt(Math.pow(canvasX - startX, 2) + Math.pow(canvasY - startY, 2))
    const distToEnd = Math.sqrt(Math.pow(canvasX - endX, 2) + Math.pow(canvasY - endY, 2))

    if (distToStart < threshold) return 'linearStart'
    if (distToEnd < threshold) return 'linearEnd'
  } else if (bgForm.value.type === 'radial') {
    const centerX = bgForm.value.radialCenter.x * canvas.width
    const centerY = bgForm.value.radialCenter.y * canvas.height
    const edgeX = bgForm.value.radialEdge.x * canvas.width
    const edgeY = bgForm.value.radialEdge.y * canvas.height

    const distToCenter = Math.sqrt(Math.pow(canvasX - centerX, 2) + Math.pow(canvasY - centerY, 2))
    const distToEdge = Math.sqrt(Math.pow(canvasX - edgeX, 2) + Math.pow(canvasY - edgeY, 2))

    if (distToCenter < threshold) return 'radialCenter'
    if (distToEdge < threshold) return 'radialEdge'
  }

  return null
}

const handleCanvasMouseDown = (event: MouseEvent) => {
  if (bgForm.value.type === 'solid') return

  const point = getPointAtPosition(event.clientX, event.clientY)
  if (point) {
    isDraggingPoint.value = true
    draggedPoint.value = point
  }
}

const handleCanvasMouseMove = (event: MouseEvent) => {
  if (!bgCanvasRef.value) return

  const canvas = bgCanvasRef.value
  
  // Update cursor based on hover
  if (!isDraggingPoint.value) {
    const point = getPointAtPosition(event.clientX, event.clientY)
    canvas.style.cursor = point ? 'pointer' : 'crosshair'
  }

  if (!isDraggingPoint.value || !draggedPoint.value) return

  const rect = canvas.getBoundingClientRect()
  const canvasX = event.clientX - rect.left
  const canvasY = event.clientY - rect.top

  // Normalize coordinates (0-1)
  const normalizedX = Math.max(0, Math.min(1, canvasX / canvas.width))
  const normalizedY = Math.max(0, Math.min(1, canvasY / canvas.height))

  if (draggedPoint.value === 'linearStart') {
    bgForm.value.linearStart = { x: normalizedX, y: normalizedY }
  } else if (draggedPoint.value === 'linearEnd') {
    bgForm.value.linearEnd = { x: normalizedX, y: normalizedY }
  } else if (draggedPoint.value === 'radialCenter') {
    bgForm.value.radialCenter = { x: normalizedX, y: normalizedY }
  } else if (draggedPoint.value === 'radialEdge') {
    bgForm.value.radialEdge = { x: normalizedX, y: normalizedY }
  }

  updatePreview()
}

const handleCanvasMouseUp = () => {
  isDraggingPoint.value = false
  draggedPoint.value = null
}

const handleCanvasMouseLeave = () => {
  if (bgCanvasRef.value) {
    bgCanvasRef.value.style.cursor = 'crosshair'
  }
  isDraggingPoint.value = false
  draggedPoint.value = null
}

// Build prompt from form data
const buildPrompt = (formType: 'bg' | 'photo' | 'sticker' = 'bg'): string => {
  let form: any
  if (formType === 'bg') {
    if (bgForm.value.type !== 'gen') {
      return ''
    }
    form = bgForm.value.genData.inputs
  } else if (formType === 'photo') {
    form = photoForm.value.genData.inputs
  } else if (formType === 'sticker') {
    form = stickerForm.value.genData.inputs
  } else {
    return ''
  }

  const parts: string[] = []
  
  if (form.generalDescription.trim()) {
    parts.push(form.generalDescription.trim())
  }
  
  if (form.overallLook.text.trim()) {
    parts.push(form.overallLook.text.trim())
  }
  
  if (form.framing.text.trim()) {
    parts.push(form.framing.text.trim())
  }
  
  // Add Object Style descriptions
  form.objectStyle.forEach((obj: any) => {
    if (obj.description.trim()) {
      parts.push(obj.description.trim())
    }
  })
  
  // Add Avoid terms
  if (form.avoid.terms.trim()) {
    const avoidTerms = form.avoid.terms.split(',').map((s: string) => s.trim()).filter((s: string) => s)
    if (avoidTerms.length > 0) {
      parts.push(`Avoid: ${avoidTerms.join(', ')}`)
    }
  }
  
  // Add Aspect Ratio to prompt
  if (form.aspectRatio) {
    const aspectRatioLabel = getAspectRatioLabel(form.aspectRatio)
    parts.push(`Aspect Ratio: ${aspectRatioLabel} (${form.aspectRatio})`)
  }
  
  return parts.join('. ')
}

// Helper function to get aspect ratio label
const getAspectRatioLabel = (value: string): string => {
  const labels: Record<string, string> = {
    '1:1': 'Square',
    '4:3': 'Standard',
    '16:9': 'Widescreen',
    '4:5': 'Portrait',
    '3:2': 'Classic',
    '9:16': 'Vertical',
    '2:3': 'Portrait',
    '3:4': 'Portrait',
    '5:4': 'Portrait',
    '21:9': 'Ultrawide'
  }
  return labels[value] || value
}

// Computed property for constructed prompt
const constructedPrompt = computed(() => {
  return buildPrompt('bg')
})

const saveBackground = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    return
  }

  if (!bgForm.value.name.trim()) {
    message.warning('Please enter a name')
    return
  }

  // Check for duplicate name
  const existingAsset = assets.value.find(
    a => a.type === 'bg' && a.name.toLowerCase() === bgForm.value.name.trim().toLowerCase()
  )
  
  if (existingAsset) {
    message.error(`An asset with the name "${bgForm.value.name}" already exists. Please choose a different name.`)
    return
  }

  isCreatingBg.value = true
  try {
    if (bgForm.value.type === 'gen') {
      // Construct narrative prompt
      const prompt = buildPrompt()
      
      // Collect all reference image IDs
      const imageIds: string[] = []
      
      if (bgForm.value.genData.inputs.overallLook.fromImageId) {
        imageIds.push(bgForm.value.genData.inputs.overallLook.fromImageId)
      }
      
      if (bgForm.value.genData.inputs.framing.fromImageId) {
        imageIds.push(bgForm.value.genData.inputs.framing.fromImageId)
      }
      
      // Collect Object Style images
      bgForm.value.genData.inputs.objectStyle.forEach((obj) => {
        if (obj.fromImageId) {
          imageIds.push(obj.fromImageId)
        }
      })
      
      // Validate aspect ratio
      if (!bgForm.value.genData.inputs.aspectRatio) {
        message.warning('Please select an aspect ratio')
        return
      }
      
      // Call generation API
      await apiClient.generateImage({
        assetType: 'bg',
        prompt,
        imageIds,
        aspectRatio: bgForm.value.genData.inputs.aspectRatio,
        projectId: selectedProjectId.value,
        name: bgForm.value.name
      })
      
      message.success('Image generated successfully')
      
      closeCreateBgModal()
      await loadAssets()
    } else {
      // For color/gradient types, render and upload image
      // Create offscreen canvas at 1080x1080
      const canvas = document.createElement('canvas')
      canvas.width = 1080
      canvas.height = 1080
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        throw new Error('Failed to get canvas context')
      }

      // Render gradient at full resolution
      renderGradient(ctx, canvas.width, canvas.height, false)

      // Convert to File
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to create blob'))
          }
        }, 'image/png')
      })
      
      const file = new File([blob], `${bgForm.value.name}.png`, { type: 'image/png' })

      // Upload via existing API
      await apiClient.uploadAsset(file, selectedProjectId.value, {
        type: 'bg',
        name: bgForm.value.name
      })

      message.success('Background created successfully')
    }

    closeCreateBgModal()
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to create background:', error)
    message.error(error?.message || 'Failed to create background')
  } finally {
    isCreatingBg.value = false
  }
}

const savePhoto = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    return
  }

  if (!photoForm.value.name.trim()) {
    message.warning('Please enter a name')
    return
  }

  // Check for duplicate name
  const existingAsset = assets.value.find(
    a => a.type === 'photo' && a.name.toLowerCase() === photoForm.value.name.trim().toLowerCase()
  )
  
  if (existingAsset) {
    message.error(`An asset with the name "${photoForm.value.name}" already exists. Please choose a different name.`)
    return
  }

  isCreatingPhoto.value = true
  try {
    // Construct narrative prompt
    const prompt = buildPrompt('photo')
    
    // Collect all reference image IDs
    const imageIds: string[] = []
    
    if (photoForm.value.genData.inputs.overallLook.fromImageId) {
      imageIds.push(photoForm.value.genData.inputs.overallLook.fromImageId)
    }
    
    if (photoForm.value.genData.inputs.framing.fromImageId) {
      imageIds.push(photoForm.value.genData.inputs.framing.fromImageId)
    }
    
    // Collect Object Style images
    photoForm.value.genData.inputs.objectStyle.forEach((obj) => {
      if (obj.fromImageId) {
        imageIds.push(obj.fromImageId)
      }
    })
    
    // Validate aspect ratio
    if (!photoForm.value.genData.inputs.aspectRatio) {
      message.warning('Please select an aspect ratio')
      return
    }
    
    // Call generation API
    await apiClient.generateImage({
      assetType: 'photo',
      prompt,
      imageIds,
      aspectRatio: photoForm.value.genData.inputs.aspectRatio,
      projectId: selectedProjectId.value,
      name: photoForm.value.name
    })
    
    message.success('Image generated successfully')
    
    closeCreatePhotoModal()
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to create photo:', error)
    message.error(error?.message || 'Failed to create photo')
  } finally {
    isCreatingPhoto.value = false
  }
}

const saveSticker = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    return
  }

  if (!stickerForm.value.name.trim()) {
    message.warning('Please enter a name')
    return
  }

  // Check for duplicate name
  const existingAsset = assets.value.find(
    a => a.type === 'sticker' && a.name.toLowerCase() === stickerForm.value.name.trim().toLowerCase()
  )
  
  if (existingAsset) {
    message.error(`An asset with the name "${stickerForm.value.name}" already exists. Please choose a different name.`)
    return
  }

  isCreatingSticker.value = true
  try {
    // Construct narrative prompt
    const prompt = buildPrompt('sticker')
    
    // Collect all reference image IDs
    const imageIds: string[] = []
    
    if (stickerForm.value.genData.inputs.overallLook.fromImageId) {
      imageIds.push(stickerForm.value.genData.inputs.overallLook.fromImageId)
    }
    
    if (stickerForm.value.genData.inputs.framing.fromImageId) {
      imageIds.push(stickerForm.value.genData.inputs.framing.fromImageId)
    }
    
    // Collect Object Style images
    stickerForm.value.genData.inputs.objectStyle.forEach((obj) => {
      if (obj.fromImageId) {
        imageIds.push(obj.fromImageId)
      }
    })
    
    // Validate aspect ratio
    if (!stickerForm.value.genData.inputs.aspectRatio) {
      message.warning('Please select an aspect ratio')
      return
    }
    
    // Call generation API
    await apiClient.generateImage({
      assetType: 'sticker',
      prompt,
      imageIds,
      aspectRatio: stickerForm.value.genData.inputs.aspectRatio,
      projectId: selectedProjectId.value,
      name: stickerForm.value.name
    })
    
    message.success('Image generated successfully')
    
    closeCreateStickerModal()
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to create sticker:', error)
    message.error(error?.message || 'Failed to create sticker')
  } finally {
    isCreatingSticker.value = false
  }
}

const openImagePicker = async (field: string, objIndex?: number | null, imgIndex?: number | null, formType: 'bg' | 'photo' | 'sticker' = 'bg') => {
  currentImagePickerField.value = field
  currentImagePickerObjIndex.value = objIndex ?? null
  // imgIndex is kept for compatibility but not used for objectStyle anymore
  currentImagePickerImgIndex.value = imgIndex ?? null
  currentImagePickerFormType.value = formType
  showImagePickerModal.value = true
  await loadPickerImages()
}

const loadPickerImages = async () => {
  isLoadingImages.value = true
  try {
    const response = await apiClient.fetchImages({
      limit: 100,
      projectId: selectedProjectId.value || undefined
    })
    pickerImages.value = response.images
  } catch (error) {
    console.error('Failed to load images:', error)
    message.error('Failed to load images')
    pickerImages.value = []
  } finally {
    isLoadingImages.value = false
  }
}

const selectImageForField = (imageId: string) => {
  if (!currentImagePickerField.value) return

  const field = currentImagePickerField.value
  const formType = currentImagePickerFormType.value
  
  if (formType === 'bg') {
    if (field === 'overallLook') {
      bgForm.value.genData.inputs.overallLook.fromImageId = imageId
    } else if (field === 'framing') {
      bgForm.value.genData.inputs.framing.fromImageId = imageId
    } else if (field === 'objectStyle') {
      const objIndex = currentImagePickerObjIndex.value
      if (objIndex !== null && bgForm.value.genData.inputs.objectStyle[objIndex]) {
        bgForm.value.genData.inputs.objectStyle[objIndex].fromImageId = imageId
      }
    }
  } else if (formType === 'photo') {
    if (field === 'overallLook') {
      photoForm.value.genData.inputs.overallLook.fromImageId = imageId
    } else if (field === 'framing') {
      photoForm.value.genData.inputs.framing.fromImageId = imageId
    } else if (field === 'objectStyle') {
      const objIndex = currentImagePickerObjIndex.value
      if (objIndex !== null && photoForm.value.genData.inputs.objectStyle[objIndex]) {
        photoForm.value.genData.inputs.objectStyle[objIndex].fromImageId = imageId
      }
    }
  } else if (formType === 'sticker') {
    if (field === 'overallLook') {
      stickerForm.value.genData.inputs.overallLook.fromImageId = imageId
    } else if (field === 'framing') {
      stickerForm.value.genData.inputs.framing.fromImageId = imageId
    } else if (field === 'objectStyle') {
      const objIndex = currentImagePickerObjIndex.value
      if (objIndex !== null && stickerForm.value.genData.inputs.objectStyle[objIndex]) {
        stickerForm.value.genData.inputs.objectStyle[objIndex].fromImageId = imageId
      }
    }
  }

  showImagePickerModal.value = false
  currentImagePickerField.value = null
  currentImagePickerObjIndex.value = null
  currentImagePickerImgIndex.value = null
  currentImagePickerFormType.value = 'bg'
}

// Object Style helper functions
const addObjectStyle = (formType: 'bg' | 'photo' | 'sticker' = 'bg') => {
  if (formType === 'bg') {
    bgForm.value.genData.inputs.objectStyle.push({
      description: '',
      fromImageId: null
    })
  } else if (formType === 'photo') {
    photoForm.value.genData.inputs.objectStyle.push({
      description: '',
      fromImageId: null
    })
  } else if (formType === 'sticker') {
    stickerForm.value.genData.inputs.objectStyle.push({
      description: '',
      fromImageId: null
    })
  }
}

const removeObjectStyle = (index: number, formType: 'bg' | 'photo' | 'sticker' = 'bg') => {
  if (formType === 'bg') {
    if (bgForm.value.genData.inputs.objectStyle.length > 1) {
      bgForm.value.genData.inputs.objectStyle.splice(index, 1)
    }
  } else if (formType === 'photo') {
    if (photoForm.value.genData.inputs.objectStyle.length > 1) {
      photoForm.value.genData.inputs.objectStyle.splice(index, 1)
    }
  } else if (formType === 'sticker') {
    if (stickerForm.value.genData.inputs.objectStyle.length > 1) {
      stickerForm.value.genData.inputs.objectStyle.splice(index, 1)
    }
  }
}

// Text Style Functions
const resetTextStyleForm = () => {
  textStyleForm.value = {
    name: 'H1',
    sampleText: 'Sample Text',
    fontFamily: 'Arial',
    fontSize: 24,
    fontWeight: '400',
    color: '#000000',
    textAlign: 'center'
  }
}

const createTextStyle = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    return
  }

  if (!textStyleForm.value.name.trim()) {
    message.warning('Please enter a name for the style')
    return
  }

  if (!textStyleForm.value.sampleText.trim()) {
    message.warning('Please enter sample text')
    return
  }

  // Check for duplicate name
  const styleName = textStyleForm.value.name.trim()
  const existingStyle = assets.value.find(
    a => a.type === 'text-style' && a.name.toLowerCase() === styleName.toLowerCase()
  )
  
  if (existingStyle) {
    message.error(`A text style with the name "${styleName}" already exists`)
    return
  }

  isCreatingTextBlock.value = true
  try {
    const metadata = {
      sampleText: textStyleForm.value.sampleText,
      fontFamily: textStyleForm.value.fontFamily,
      fontSize: textStyleForm.value.fontSize,
      fontWeight: textStyleForm.value.fontWeight,
      color: textStyleForm.value.color,
      textAlign: textStyleForm.value.textAlign
    }

    await apiClient.createAsset({
      projectId: selectedProjectId.value,
      type: 'text-style',
      name: styleName,
      metadata: JSON.stringify(metadata)
    })

    message.success('Text style created successfully')
    resetTextStyleForm()
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to create text style:', error)
    message.error(error?.message || 'Failed to create text style')
  } finally {
    isCreatingTextBlock.value = false
  }
}

// Text Copy Functions
const resetTextCopyForm = () => {
  textCopyForm.value = {
    name: 'Headers',
    items: ['']
  }
}

const addCopyItem = (index: number) => {
  if (index === -1) {
    textCopyForm.value.items.push('')
  } else {
    textCopyForm.value.items.splice(index + 1, 0, '')
  }
}

const removeCopyItem = (index: number) => {
  if (textCopyForm.value.items.length > 1) {
    textCopyForm.value.items.splice(index, 1)
  }
}

const createTextCopy = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project')
    return
  }

  if (!textCopyForm.value.name.trim()) {
    message.warning('Please enter a name for the text set')
    return
  }

  const validItems = textCopyForm.value.items.filter(item => item.trim())
  if (validItems.length === 0) {
    message.warning('Please enter at least one text item')
    return
  }

  isCreatingTextBlock.value = true
  try {
    const metadata = {
      items: validItems
    }

    await apiClient.createAsset({
      projectId: selectedProjectId.value,
      type: 'text-copy',
      name: textCopyForm.value.name,
      metadata: JSON.stringify(metadata)
    })

    message.success('Text copy set created successfully')
    resetTextCopyForm()
    await loadAssets()
  } catch (error: any) {
    console.error('Failed to create text copy set:', error)
    message.error(error?.message || 'Failed to create text copy set')
  } finally {
    isCreatingTextBlock.value = false
  }
}

// Watch for type changes
watch(selectedType, async () => {
  if (selectedType.value === 'reference') {
    await loadAssets()
    if (moodboards.value.length === 0 && props.projectId) {
      await ensureReferenceAndMoodboard()
    }
    if (moodboards.value.length > 0 && !selectedMoodboardId.value) {
      selectedMoodboardId.value = moodboards.value[0].id
      loadMoodboardImages(moodboards.value[0].id)
    }
  }
  // Clear upload file lists when switching types
  bgUploadFileList.value = []
  logoUploadFileList.value = []
  photoUploadFileList.value = []
})

// Watch bgForm for changes to update preview
watch(
  () => [bgForm.value.type, bgForm.value.color1, bgForm.value.color2],
  () => {
    if (showCreateBgModal.value) {
      updatePreview()
    }
  },
  { deep: true }
)

// Watch modal open to render preview
watch(showCreateBgModal, (isOpen) => {
  if (isOpen) {
    // Wait for next tick to ensure canvas is rendered
    setTimeout(() => {
      updatePreview()
    }, 100)
  }
})
</script>

<style scoped>
.assets-manager {
  height: 100%;
  overflow-y: auto;
  padding: 0;
  background: var(--base-color);
}

.assets-container {
  width: 100%;
  padding: 0 24px;
}

.assets-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 12px 16px;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.assets-info {
  flex: 1;
}

.assets-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--text-color);
}


.type-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  padding: 16px;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  flex-wrap: wrap;
}

.type-tab {
  padding: 6px 12px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-color-2);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.type-tab:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.type-tab.active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.assets-content {
  position: relative;
  min-height: 400px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.moodboards-section,
.assets-grid-section {
  padding: 16px 0;
  display: flex;
  gap: 16px;
}

.assets-grid-section.with-text-editor,
.assets-grid-section.with-actions {
  flex-direction: row;
  align-items: flex-start;
}

.assets-grid-container {
  flex: 1;
}

.assets-grid-container.with-editor {
  flex: 2;
}

.assets-grid-container.with-actions {
  flex: 1;
}

.actions-panel {
  flex: 0 0 220px;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-left: 16px;
  min-width: 0;
}


.actions-upload-dragger {
  width: 100%;
  min-height: 120px;
  padding: 20px;
  background: var(--card-color);
  border: 2px dashed var(--border-color);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.actions-upload-dragger:hover {
  border-color: var(--primary-color);
  background: rgba(24, 160, 88, 0.05);
}

.actions-upload-dragger :deep(.n-upload-dragger) {
  background: transparent !important;
  border: none !important;
  padding: 0;
}

.action-button-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.text-editor-panel {
  flex: 1;
  min-width: 300px;
  max-width: 400px;
  background: var(--card-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.text-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--base-color);
}

.text-editor-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-color);
}

.close-editor-button {
  background: transparent;
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-color-2);
  transition: all 0.2s ease;
}

.close-editor-button:hover {
  background: var(--hover-color);
  color: var(--text-color);
}

.text-editor-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.text-settings-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 16px;
}

.text-settings-grid .n-form-item {
  margin-bottom: 0;
}

.text-editor-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}


.section-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-color);
}

.create-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-button:hover {
  background: #16a34a;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.moodboards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.moodboard-card {
  padding: 16px;
  background: var(--card-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.moodboard-card:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(24, 160, 88, 0.2);
}

.moodboard-card h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color);
}

.moodboard-description {
  margin: 0 0 12px 0;
  font-size: 13px;
  color: var(--text-color-2);
}

.moodboard-preview {
  padding: 12px;
  background: var(--base-color);
  border-radius: 4px;
  min-height: 60px;
}

.moodboard-detail,
.moodboard-detail-panel {
  padding: 16px 0;
}

.moodboard-detail-placeholder {
  padding: 24px 0;
}

.asset-card.moodboard-card {
  cursor: pointer;
}

.asset-card.moodboard-card.selected {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(24, 160, 88, 0.3);
}

.moodboard-card-preview {
  position: relative;
}

.moodboard-card-preview .moodboard-thumbnail {
  width: 100%;
  aspect-ratio: 1;
  background: var(--base-color);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.moodboard-card-preview .moodboard-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.moodboard-info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.moodboard-info-row > div {
  flex: 1;
}

.moodboard-info {
  padding: 16px;
  background: var(--card-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.moodboard-info h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-color);
}

.moodboard-info .moodboard-description {
  margin: 0;
  font-size: 14px;
  color: var(--text-color-2);
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  color: var(--text-color-2);
}

.moodboard-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.moodboard-image-card {
  background: var(--card-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.moodboard-image-card:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(24, 160, 88, 0.2);
  transform: translateY(-2px);
}

.moodboard-image-card .image-preview {
  width: 100%;
  aspect-ratio: 1;
  background: var(--base-color);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.moodboard-image-card .image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-count {
  font-size: 12px;
  color: var(--text-color-2);
}

.empty-moodboard {
  font-size: 12px;
  color: var(--text-color-3);
  font-style: italic;
}

.assets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.asset-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.asset-card {
  position: relative;
  background: var(--card-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.asset-card:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(24, 160, 88, 0.2);
  transform: translateY(-2px);
}

.asset-preview {
  width: 100%;
  aspect-ratio: 1;
  background: var(--base-color);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

.asset-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

.asset-card:hover .asset-overlay {
  opacity: 1;
}

.view-button,
.delete-button {
  background: rgba(128, 128, 128, 0.9);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.view-button:hover {
  background: var(--primary-color);
  color: white;
  transform: scale(1.1);
}

.delete-button:hover {
  background: #ef4444;
  color: white;
  transform: scale(1.1);
}

/* Asset Modal */
.asset-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.asset-modal {
  background: var(--card-color);
  border-radius: 12px;
  max-width: 95vw;
  max-height: 95vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.asset-modal-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--base-color);
}

.asset-modal-close {
  background: transparent;
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-color-2);
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.asset-modal-close:hover {
  background: var(--hover-color);
  color: var(--text-color);
}

.asset-modal-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: calc(95vh - 80px);
  height: 100%;
}

.asset-modal-body {
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: hidden;
  height: 100%;
}

.asset-modal-image-section {
  width: 75%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--base-color);
  padding: 20px;
  overflow: auto;
}

.asset-modal-palette-section {
  width: 75%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--base-color);
  padding: 20px;
  overflow: auto;
}

.palette-modal-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
}

.palette-modal-header h3 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: var(--text-color);
}

.palette-count {
  margin: 0;
  font-size: 14px;
  color: var(--text-color-2);
}

.palette-modal-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
}

.palette-modal-swatch {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  border: 2px solid var(--border-color);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
}

.palette-modal-swatch:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border-color: var(--primary-color);
}

.palette-modal-swatch-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

.palette-modal-swatch:hover .palette-modal-swatch-overlay {
  opacity: 1;
}

.palette-swatch-action-button {
  background: rgba(60, 60, 60, 0.95);
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
  padding: 0;
}

.palette-swatch-action-button:hover {
  background: rgba(80, 80, 80, 0.95);
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.palette-swatch-delete-button:hover {
  background: #ff4d4f;
  color: white;
}

.palette-modal-swatch-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 8px;
  font-size: 11px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  text-align: center;
  backdrop-filter: blur(4px);
  z-index: 5;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}

.palette-modal-empty {
  width: 100%;
  text-align: center;
  padding: 40px 20px;
  color: var(--text-color-2);
  font-size: 14px;
}

.palette-modal-swatch.label-assignment-active {
  cursor: pointer;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(24, 160, 88, 0.3);
}

.palette-modal-swatch.label-assignment-active:hover {
  transform: scale(1.1);
  box-shadow: 0 0 0 3px rgba(24, 160, 88, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3);
}

.palette-label-assignment {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);
}

.label-assignment-table {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.label-assignment-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: var(--base-color);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.label-assignment-row:hover {
  background: var(--card-color);
  border-color: var(--primary-color);
}

.label-assignment-row.active {
  background: rgba(24, 160, 88, 0.1);
  border-color: var(--primary-color);
  border-width: 2px;
  box-shadow: 0 0 0 2px rgba(24, 160, 88, 0.2);
}

.label-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-color);
  text-transform: capitalize;
}

.label-swatch {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: 1px solid #666;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.label-assignment-row.active .label-swatch {
  border: 1px solid var(--primary-color);
  box-shadow: 0 0 0 2px rgba(24, 160, 88, 0.3);
}

.label-swatch-empty {
  color: var(--text-color-3);
  font-size: 18px;
  font-weight: 300;
}

.label-assignment-hint {
  margin: 12px 0 0 0;
  padding: 8px 12px;
  background: rgba(24, 160, 88, 0.1);
  border: 1px solid rgba(24, 160, 88, 0.3);
  border-radius: 4px;
  font-size: 13px;
  color: var(--text-color);
}

.label-assignment-hint strong {
  color: var(--primary-color);
  text-transform: capitalize;
}

.asset-modal-data-section {
  width: 25%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--border-color);
  background: var(--card-color);
  overflow-y: auto;
  padding: 20px;
}

@media (max-width: 1024px) {
  .asset-modal-body {
    flex-direction: column;
  }
  
  .asset-modal-image-section,
  .asset-modal-palette-section {
    width: 100%;
    height: 60vh;
  }
  
  .asset-modal-data-section {
    width: 100%;
    height: auto;
    border-left: none;
    border-top: 1px solid var(--border-color);
    max-height: 40vh;
  }
}

.asset-modal-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  background: var(--base-color);
  display: block;
}

.asset-modal-section {
  margin-bottom: 24px;
}

.asset-modal-section:last-child {
  margin-bottom: 0;
}

.asset-modal-section-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.asset-modal-metadata {
  margin-bottom: 0;
}

.metadata-line {
  margin: 8px 0;
  font-size: 14px;
  color: var(--text-color);
  line-height: 1.5;
}

.asset-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
}

.asset-modal-action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-color);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  width: 100%;
  justify-content: center;
}

.asset-modal-action-button:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(24, 160, 88, 0.1);
}

.asset-modal-action-button:first-child:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.asset-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.text-style-preview,
.text-copy-preview,
.color-palette-preview {
  width: 100%;
  height: 100%;
  background: var(--base-color);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.text-style-preview .preview-content {
  width: 100%;
  text-align: center;
  word-wrap: break-word;
  overflow: hidden;
}

.text-copy-preview .preview-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.copy-preview-label {
  font-size: 10px;
  text-transform: uppercase;
  color: var(--text-color-2);
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.copy-preview-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 100%;
  overflow-y: auto;
}

.copy-preview-item {
  font-size: 11px;
  color: var(--text-color);
  padding: 4px 8px;
  background: var(--card-color);
  border-radius: 4px;
  border: 1px solid var(--border-color);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.color-palette-preview .palette-preview-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.palette-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.palette-swatch-preview {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.palette-swatch-preview:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.palette-empty {
  font-size: 11px;
  color: var(--text-color-2);
  text-align: center;
  width: 100%;
}

.text-preview {
  margin-top: 16px;
  padding: 12px;
  background: var(--base-color);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.preview-label {
  font-size: 12px;
  color: var(--text-color-2);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-text {
  min-height: 60px;
  padding: 12px;
  background: var(--card-color);
  border-radius: 4px;
  word-wrap: break-word;
}

.copy-items-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.copy-item-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.copy-item-row .n-input {
  flex: 1;
}

.asset-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--text-color-3);
}

.asset-info {
  padding: 12px;
}

.asset-info h4 {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.asset-type {
  margin: 0 0 8px 0;
  font-size: 12px;
  color: var(--text-color-2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.asset-meta {
  font-size: 11px;
  color: var(--text-color-3);
}

.no-project-selected {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.upload-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.upload-card {
  position: relative;
  background: rgba(24, 160, 88, 0.05);
  border: 2px dotted var(--primary-color);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  aspect-ratio: 1;
  width: 100%;
}

.upload-card:hover {
  border-color: var(--primary-color);
  background: rgba(24, 160, 88, 0.1);
  box-shadow: 0 4px 12px rgba(24, 160, 88, 0.2);
}

.upload-dragger {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: transparent !important;
  border: none !important;
  min-height: 100%;
  gap: 8px;
}

.upload-dragger:hover {
  background: transparent !important;
}

.upload-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  flex-shrink: 0;
}

.upload-card:hover .upload-icon-wrapper {
  color: var(--primary-color);
  transform: scale(1.1);
  transition: transform 0.2s ease;
}

.create-bg-card {
  cursor: pointer;
}

.create-bg-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.bg-creator-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.bg-creator-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.bg-creator-form :deep(.n-form-item) {
  margin-bottom: 0;
}

.bg-creator-form :deep(.n-form-item-label) {
  padding-bottom: 4px;
  font-size: 13px;
}

.bg-creator-form :deep(.n-form-item-feedback-wrapper) {
  min-height: 0;
}

.bg-preview-section {
  flex: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}

.canvas-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 16px;
  background: var(--base-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.bg-preview-canvas {
  border-radius: 4px;
  cursor: crosshair;
  display: block;
  max-width: 100%;
  height: auto;
}

.bg-preview-canvas.dragging {
  cursor: grabbing !important;
}

.canvas-info {
  margin-top: 8px;
  text-align: center;
}

.name-type-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
}

.color-pickers-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  width: 100%;
}

.color-pickers-row :deep(.n-color-picker) {
  flex-shrink: 0;
  min-width: 130px;
  width: 130px;
}

.color-pickers-row.single-color :deep(.n-color-picker) {
  width: 100%;
  max-width: 200px;
}

.color-pickers-row :deep(.n-color-picker-trigger) {
  width: 100%;
}

.color-pickers-row :deep(.n-color-picker-trigger__value) {
  width: 100%;
  min-width: 0;
  overflow: visible;
}

.swap-button {
  flex-shrink: 0;
  min-width: 32px;
  width: 32px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.palette-selector-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.palette-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
}

.palette-swatch {
  width: 28px;
  height: 28px;
  border-radius: 3px;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.palette-swatch:hover {
  transform: scale(1.1);
  border-color: var(--primary-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 1;
  position: relative;
}

.gen-label-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
}

.label-image-button {
  flex-shrink: 0;
  min-width: 20px;
  width: 20px;
  height: 20px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gen-color-palette {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gen-objects-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gen-object-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gen-form-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: var(--base-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.image-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
}

.image-picker-item {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid transparent;
}

.image-picker-item:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(24, 160, 88, 0.2);
}

.image-picker-preview {
  width: 100%;
  aspect-ratio: 1;
  background: var(--base-color);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.image-picker-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-picker-name {
  margin: 8px 0 0 0;
  font-size: 11px;
  color: var(--text-color-2);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 4px;
}
</style>

